import { useState, useEffect, useCallback } from "react"
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Doughnut, Bar } from "react-chartjs-2"
import axios from "axios"
import getTimeZoneDateRange from "../../utils/getTimeZoneDateRange"
import SessionDataManager from "./SessionDataManager"


ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartDataLabels
)

// =============================================================================
// CONSTANTS
// =============================================================================
const SCREEN_LABELS = {
  landing: "Landing Page",
  problem_area: "Problem Area",
  pain_location: "Pain Location",
  cpg_questions: "CPG Questions",
  subtype_questioning: "Subtype Questions",
  self_assessment: "Self Assessment",
  nprs_symptoms: "NPRS/Symptoms",
  pre_nprs: "NPRS/Symptoms",
  movement_test_reveal: "Movement Test Reveal",      
  movement_reveal : "Movement Test Reveal",   
  safety_screen: "Safety Screen",
  safety_screening: "Safety Screen",   
  test1_demo: "Demo Video 1",
  camera_preview: "Camera Preview",
  test1_ar: "Movement Test 1",
  movement_test_1 : "Movement Test 1",
  movement_q1 : "Movement Test - Q1",
  movement_q2 : "Movement Test - Q2",
  self_report_compare: "Self Report - Compare", 
  self_report_speed: "Self Report - Speed",
  test1_rpe: "RPE",
  test1_symptoms: "Symptoms (Test 1)",
  symptom1 : "Symptoms (Test 1)",
  test2_demo: "Demo Video 2",
  test2_ar: "Movement Test 2",
  test2_symptoms: "Symptoms (Test 2)",
  symptom2: "Symptoms (Test 2)",
  personalize: "Demographics",
  recovery_score: "Recovery Score",
  plan_phase1: "Viewed Phase 1",
  plan_phase2: "Viewed Phase 2",
  plan_phase3: "Viewed Phase 3",
  plan_screen: "Plan Screen",
  screen_plan: "Plan Screen",
}
const SCREEN_ORDER = {
  landing: 0,
  landing_video: 1,
  problem_area: 2,
  pain_location: 3,
  cpg_questions: 4,
  subtype_questioning: 5,
  self_assessment: 6,
  nprs_symptoms: 7,
  pre_nprs : 8,
  movement_reveal : 9,
  movement_test_reveal: 10,
  safety_screening :11,
  safety_screen: 12,
  test1_demo: 13,
  camera_preview: 14,
  test1_ar: 15,
  movement_test_1: 16,
  movement_q1: 17,
  movement_q2: 18,
  self_report_compare: 19,
  self_report_speed: 20,
  test1_rpe: 21,
  test1_symptoms: 22,
  symptom1: 23,
  test2_demo: 24,
  test2_ar: 25,
  test2_symptoms: 26,
  symptom2: 27,
  personalize: 28,
  recovery_score: 29,
  plan_phase1: 30,
  plan_phase2: 31,
  plan_phase3: 32,
  plan_screen: 33,
};
const sortScreenFlow = (flow) => {
  return [...flow].sort((a, b) => {
    const orderA = SCREEN_ORDER[a.screen] ?? Number.MAX_SAFE_INTEGER;
    const orderB = SCREEN_ORDER[b.screen] ?? Number.MAX_SAFE_INTEGER;

    // first sort by screen order
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // if same screen, keep chronological order
    return (a.enter || 0) - (b.enter || 0);
  });
};

const TOUCHPOINT_LABELS = {
  landing: 'Landing Page',
  post_plan: 'Post-Assessment',
  post_session: 'In-App (Post-Session)'
}

// Helper function to format duration in seconds
const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "—"
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
}

// Helper to format date only (e.g., "Dec 18, 2025")
const formatDate = (date) => {
  if (!date) return "—"
  // If date-only string (YYYY-MM-DD), parse parts directly to avoid UTC timezone shift
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number)
    const dt = new Date(year, month - 1, day) // month is 0-indexed
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  const dt = new Date(date)
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Helper function to format ISO date-time string (e.g., "Dec 18, 10:40 PM")
const formatDateTime = (date) => {
  if (!date) return "—"
  const dt = new Date(date)
  const month = dt.toLocaleString('en-US', { month: 'short' })
  const day = dt.getDate()
  const hour = dt.getHours()
  const minute = dt.getMinutes().toString().padStart(2, '0')
  const hour12 = hour % 12 || 12
  const ampm = hour >= 12 ? 'PM' : 'AM'
  return `${month} ${day}, ${hour12}:${minute} ${ampm}`
}

// Helper function to format exit reasons
const formatExitReason = (reason) => {
  if (!reason) return null
  const mapping = {
    camera_permission_denied: "Camera denied",
    ar_init_failed: "AR failed",
    user_skipped: "Skipped",
    timeout: "Timeout",
    browser_not_supported: "Browser unsupported",
    device_not_supported: "Device unsupported",
    error_unknown: "Error",
    abandoned: "Abandoned",
  }
  return mapping[reason] || reason.replace(/_/g, ' ')
}
// Helper to render a subscription plan label
const formatSubscriptionPlan = (plan) => {
  if (!plan) return null
  // plan comes in as "1x week" / "8x week" etc from the backend
  const [countStr, interval] = plan.split('x ')
  const count = parseInt(countStr, 10) || 1
  if (!interval) return plan
  const intervalLabel = interval.charAt(0).toUpperCase() + interval.slice(1)
  return count === 1 ? `Weekly` : `${count}-${intervalLabel}`
}
// Helper to render the GSA tyre_plan value
const formatTyrePlanLabel = (plan) => {
  const labels = { weekly: 'Weekly', four: '4-Week', eight: '8-Week' }
  return labels[plan] || plan || null
}

// =============================================================================
// COLLAPSIBLE SECTION COMPONENT
// =============================================================================
const CollapsibleSection = ({ id, title, defaultExpanded = false, children, badge = null }) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="border border-slate-200 rounded-lg mb-3 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white hover:bg-slate-100 transition-colors ${expanded ? 'border-b border-slate-200' : ''}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{title}</span>
          {badge && <span className="text-xs text-slate-400">{badge}</span>}
        </div>
        <svg
          className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </div>
  )
}

// =============================================================================
// DRILL-DOWN MODAL COMPONENT
// =============================================================================
const DrillDownModal = ({ isOpen, onClose, stepData, users, loading, pagination, onPageChange, onSessionClick, onDownload, onNewTab }) => {
  if (!isOpen) return null

  // Always show chat column since we now have L/A/S indicators for all touchpoints
  const statusColors = {
  "Signed up": "bg-blue-100 text-blue-700",
  "Completed GSA": "bg-green-100 text-green-700",
  "Started GSA": "bg-yellow-100 text-yellow-700",
  "LP Chat": "bg-purple-100 text-purple-700",
  "Blank": "bg-gray-100 text-gray-700",
};

  // C5: Detect exit drilldown to show extra columns
  const isExitDrilldown = stepData?.id === 'exited_gsa';
  const isPlanDrilldown = stepData?.id === 'plan_selected' || stepData?.id === 'plan_not_selected';


  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={`bg-white rounded-xl shadow-2xl w-full ${isExitDrilldown ? 'max-w-7xl' : 'max-w-5xl'} max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Users at: <span className="text-teal-600">{stepData?.name}</span>
            </h2>     
            <p className="text-sm text-slate-500 mt-0.5">
              {(pagination?.total ?? stepData?.count ?? 0).toLocaleString()} users
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(users, stepData)}
              className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium"
              title="Download current page as CSV"
            >
              ↓ CSV
            </button>
            <button
              onClick={() => onNewTab(users, stepData)}
              className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium"
              title="Open in new tab"
            >
              ↗ New Tab
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading users...</div>
          ) : (
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Site
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Date / Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  {/* {isPlanDrilldown && ( */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      Plan
                    </th>
                  {/* )} */}
                  {isExitDrilldown && (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                        Exited At
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                        Exit Reason
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                        Feedback
                      </th>
                    </>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" title="L=Landing, A=Post-Plan, S=Post-Session">
                    Chat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Time on Step
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Device
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-slate-500">
                      No users found at this step.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => (
                    <tr 
                    key={user.session_id || index} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => onSessionClick(user.session_id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {(() => {
                          const site = user.device_type ? user.device_type.split('|')[1]?.trim() : null;
                          return site ? (
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              site.toLowerCase().includes('startfreept.com')
                                ? 'bg-teal-100 text-teal-700'
                                : site.toLowerCase().includes('myptmyra')
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-purple-100 text-purple-700'
                            }`}>
                              {site.toLowerCase().includes('startfreept.com') ? 'StartFreePT' : site.toLowerCase().includes('myptmyra') ? 'MyPTMyra' : 'Virdio'}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          );
                        })()}
            
            {user.google_user || user.returning_user || user.cancel_mic ? <br /> : ''}
            {user.google_user ? <span className={`px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700`} title="Google User">G</span> : ''}
            {user.returning_user ? <span className={`px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700`} title="Returning user">R</span> : ''}
            {user.cancel_mic ? <span className={`px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700`} title="Cancel Mic">M</span> : ''}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDateTime(user.session_start_time)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {user.city && user.country
                          ? `${user.city}, ${user.country}`
                          : <span className="italic text-slate-400">Unknown</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {user.status && user.status !== 'Blank' ? (
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                            user.status_type === 'success'
                              ? 'bg-green-100 text-green-700'
                              : user.status_type === 'exit'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}>
                            {user.status}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* {isPlanDrilldown && ( */}
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {user.tyre_plan ? (
                            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
                              {formatTyrePlanLabel(user.tyre_plan)}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      {/* )} */}
                      {isExitDrilldown && (
                        <>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {user.gsa_step ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                                {user.gsa_step.replace(/_/g, ' ')}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {user.exit_reason ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                                {user.exit_reason.replace(/_/g, ' ')}
                              </span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 max-w-[180px] truncate" title={user.feedback || ''}>
                            {user.feedback || <span className="text-slate-300">—</span>}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {/* L=Landing, A=Post-Plan (Assessment), S=Post-Session */}
                        {user.chat_touchpoints ? (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700" title="L=Landing, A=Post-Plan, S=Post-Session">
                            {user.chat_touchpoints}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDuration(user.time_on_step_sec)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {user.device_type
                          ? user.device_type.split('|')[0]?.trim().charAt(0).toUpperCase() + user.device_type.split('|')[0]?.trim().slice(1)
                          : "Unknown"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous {pagination.limit}
          </button>
          <span className="text-sm text-slate-500">
            {pagination.total === 0
              ? 'No results'
              : `Showing ${((pagination.page - 1) * pagination.limit) + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}`
            }
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next {pagination.limit} →
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}

// =============================================================================
// SESSION DETAILS MODAL COMPONENT (Enhanced with Collapsible Sections)
// =============================================================================
const SessionDetailModal = ({ isOpen, onClose, sessionDetails, loading, onDownload, onNewTab }) => {
  // Filter state for the Test 1 dashboard timeline. Declared above the
  // early-return so the hook order stays stable across renders. Reset to
  // "key" each time the modal closes via the useEffect below.
  const [currentFilter, setCurrentFilter] = useState("key")
  useEffect(() => {
    if (!isOpen) setCurrentFilter("key")
  }, [isOpen])

  if (!isOpen) return null;

  // The API now returns a structured object (not an array)
  const data = sessionDetails;
  console.log("Session details data:", data)


  // Helper to determine status
  const getStatus = () => {
    if (!data) return { text: '', badge: '' }
    if (data.signed_up || data.final_conversion_type === 'signup') return { text: 'Signed Up', badge: 'bg-green-100 text-green-700' }
    if (data.recovery_score_viewed || data.viewed_plan) return { text: 'Completed GSA', badge: 'bg-blue-100 text-blue-700' }
    if (data.started_assessment) return { text: 'Started GSA', badge: 'bg-yellow-100 text-yellow-700' }
    if (data.chats && Object.keys(data.chats).length > 0) return { text: 'LP Chat', badge: 'bg-purple-100 text-purple-700' }
    return { text: '', badge: '' }
  }

  const status = getStatus()

  // Calculate total journey duration from screen_flow
  const screenFlow = data?.screen_flow || []
    console.log("screenFlow : ",screenFlow)
    console.log("screenFlow2 : ",data)
  // const totalDuration = screenFlow.reduce((sum, s) => sum + (s.duration || 0), 0)
  const totalDuration = Math.round(screenFlow.reduce((sum, s) => sum + (s.duration || 0), 0) / 1000)
  console.log("Screen flow data:", screenFlow)

  // Format primary goals
  const formatGoals = (goals) => {
    if (!goals) return '—'
    if (Array.isArray(goals)) return goals.map(g => g.text || g).join(', ')
    return goals
  }

  // Render ROM bar chart
  const renderROMChart = (romData) => {
    if (!romData || !Array.isArray(romData) || romData.length === 0) return null
    return (
      <div className="mt-3">
        <p className="text-xs text-slate-500 mb-2">ROM by Rep ({romData.length} reps)</p>
        <div className="flex gap-1 items-end h-8">
          {romData.map((r, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-teal-600 to-teal-400 rounded-sm transition-all"
              style={{ height: `${r}%` }}
              title={`Rep ${i + 1}: ${r}%`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>Rep 1</span>
          <span>Rep {romData.length}</span>
        </div>
      </div>
    )
  }
  
	// Render Test Events Table
	const renderEventsList = (eventsData) => {
	  if (!Array.isArray(eventsData) || eventsData.length === 0) {
		return null;
	  }

	  const formatValue = (value) => {
		if (value === true) return "Yes";
		if (value === false) return "No";
		if (Array.isArray(value)) return value.join(", ");
		if (typeof value === "object" && value !== null) {
		  return JSON.stringify(value);
		}
		return String(value);
	  };

	  const formatLabel = (value) => {
		if (!value) return "—";
		return String(value).replace(/_/g, " ");
	  };

	  const formatEventTime = (timestamp) => {
		if (!timestamp) return "—";

		const date = new Date(timestamp);

		if (Number.isNaN(date.getTime())) {
		  return "—";
		}

		return date.toLocaleTimeString([], {
		  hour: "2-digit",
		  minute: "2-digit",
		  second: "2-digit",
		});
	  };

	  const getEventBadgeClass = (eventName) => {
		switch (eventName) {
		  case "step_changed":
			return "bg-blue-50 text-blue-700 border-blue-100";
		  case "rep_completed":
			return "bg-teal-50 text-teal-700 border-teal-100";
		  case "completed":
			return "bg-green-50 text-green-700 border-green-100";
		  case "exercise_started":
			return "bg-amber-50 text-amber-700 border-amber-100";
		  default:
			return "bg-slate-50 text-slate-700 border-slate-100";
		}
	  };

	  const normalizeEvent = (eventItem) => {
		const isTuple = Array.isArray(eventItem);

		const tupleEventName = isTuple ? eventItem[0] : null;

		const rawParams = isTuple
		  ? eventItem[1]
		  : eventItem?.params || eventItem || {};

		const params =
		  rawParams && typeof rawParams === "object" && !Array.isArray(rawParams)
			? rawParams
			: {};

		const eventName =
		  tupleEventName ||
		  eventItem?.eventName ||
		  eventItem?.event_name ||
		  params.event_name ||
		  "unknown_event";

		const ignoredDetailKeys = new Set([
		  "event_name",
		  "event_timestamp",
		  "timestamp",
		  "context_type",
		  "gsa_step",
		  "step",
		  "test1_rep_count",
		  "test2_rep_count",
		  "rep_number",
		]);

		const details = Object.entries(params).filter(([key, value]) => {
		  return (
			!ignoredDetailKeys.has(key) &&
			value !== null &&
			value !== undefined &&
			value !== ""
		  );
		});

		return {
		  eventName,
		  eventTimestamp: params.event_timestamp || params.timestamp || null,
		  context: params.context_type || "—",
		  step: params.step || params.gsa_step || "—",
		  repNumber: params.rep_number ?? "—",
		  repCount: params.test1_rep_count ?? params.test2_rep_count ?? "—",
		  details,
		  params,
		};
	  };

	  return (
		<div className="mt-3">
		  <div className="flex items-center justify-between mb-2">
			<p className="text-xs text-slate-500">
			  Events ({eventsData.length})
			</p>
		  </div>

		  <div className="overflow-x-auto rounded-lg border border-slate-200">
			<table className="min-w-full text-xs">
			  <thead className="bg-slate-50 text-slate-500">
				<tr>
				  <th className="px-3 py-2 text-left font-medium w-12">#</th>
				  <th className="px-3 py-2 text-left font-medium">Time</th>
				  <th className="px-3 py-2 text-left font-medium">Event</th>
				  <th className="px-3 py-2 text-left font-medium">Step</th>
				  <th className="px-3 py-2 text-left font-medium">Rep</th>
				  <th className="px-3 py-2 text-left font-medium">Count</th>
				  <th className="px-3 py-2 text-left font-medium">Details</th>
				</tr>
			  </thead>

			  <tbody className="divide-y divide-slate-100 bg-white">
				{eventsData.map((eventItem, i) => {
				  const event = normalizeEvent(eventItem);

				  return (
					<tr key={i} className="hover:bg-slate-50 align-top">
					  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
						{i + 1}
					  </td>

					  <td
						className="px-3 py-2 text-slate-500 whitespace-nowrap font-mono"
						title={event.eventTimestamp || ""}
					  >
						{formatEventTime(event.eventTimestamp)}
					  </td>

					  <td className="px-3 py-2 whitespace-nowrap">
						<span
						  className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${getEventBadgeClass(
							event.eventName
						  )}`}
						>
						  {event.eventName}
						</span>
					  </td>

					  <td className="px-3 py-2 text-slate-600">
						{formatLabel(event.step)}
					  </td>

					  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
						{event.repNumber}
					  </td>

					  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
						{event.repCount}
					  </td>

					  <td className="px-3 py-2 text-slate-600">
						{event.details.length > 0 ? (
						  <div className="flex flex-wrap gap-1">
							{event.details.map(([key, value]) => (
							  <span
								key={key}
								className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600"
								title={formatValue(value)}
							  >
								<span className="font-medium">
								  {formatLabel(key)}:
								</span>
								<span className="ml-1">
								  {formatValue(value)}
								</span>
							  </span>
							))}
						  </div>
						) : (
						  <span className="text-slate-400">—</span>
						)}
					  </td>
					</tr>
				  );
				})}
			  </tbody>
			</table>
		  </div>
		</div>
	  );
	};

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[55]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-teal-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Session Details</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-slate-500 font-mono">{data?.session_id}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  navigator.clipboard.writeText(data?.session_id || '')
                }}
                className="text-xs text-slate-400 hover:text-teal-600 transition-colors"
                title="Copy Session ID"
              >
                📋
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDownload(data)}
              disabled={!data}
              className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              title="Download session as CSV"
            >
              ↓ CSV
            </button>
            <button
              onClick={() => onNewTab (data)}
              disabled={!data}
              className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              title="Open session in new tab"
            >
              ↗ New Tab
            </button>
            <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-700">&times;</button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading session details...</div>
          ) : !data ? (
            <div className="text-center text-gray-500 py-10">No details found.</div>
          ) : (
            <div className="space-y-0">
              {/* SECTION 1: Overview */}
              <CollapsibleSection id="overview" title="📋 Overview" defaultExpanded={true}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Device</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{data.device_type || '—'}</p>
                    {data.device_info && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate" title={data.device_info}>
                        {data.device_info.substring(0, 35)}...
                      </p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Location</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {[data.city, data.country].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Session Started</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {formatDateTime(data.session_start_time)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Status</p>
                    {status.text ? (
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded-full ${status.badge}`}>
                        {status.text}
                      </span>
                    ) : (
                      <p className="text-sm text-slate-400 mt-1">—</p>
                    )}
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION 2: Journey Timeline */}
              <CollapsibleSection
                id="journey"
                title="🗺️ Journey Timeline"
                defaultExpanded={true}
                badge={screenFlow.length > 0 ? `${screenFlow.length} screens, ${formatDuration(totalDuration)} total` : null}
              >
                
                {screenFlow.length === 0 ? (
                  <p className="text-sm text-slate-500">No journey data available.</p>
                ) : (
                  <div className="space-y-0">
                    {sortScreenFlow(screenFlow).map((step, i) => {
                      const isCompleted = step.completed
                      const isFailed = step.exitReason && ['camera_permission_denied', 'ar_init_failed', 'abandoned', 'timeout'].includes(step.exit_reason)
                      return (
                        <div
                          key={i}
                          className={`flex items-center py-2 border-l-2 ml-2 pl-4 relative ${
                            isFailed ? 'border-red-300' : isCompleted ? 'border-teal-300' : 'border-slate-200'
                          }`}
                        >
                          <div
                            className={`absolute -left-[5px] w-2 h-2 rounded-full ${
                              isFailed ? 'bg-red-500' : isCompleted ? 'bg-teal-500' : 'bg-slate-300'
                            }`}
                          />
                          <div className="flex-1 flex items-center justify-between">
                            <div>
                              <span className="text-sm font-medium text-slate-700">
                                {SCREEN_LABELS[step.screen] || step.screen}
                              </span>
                              {isFailed && (
                                <span className="ml-2 text-xs text-red-500">
                                  ({formatExitReason(step.exitReason)})
                                </span>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-slate-600">{formatDuration(Math.round(step.duration / 1000))}</span>
                              <span className="text-xs text-slate-400 ml-2">
                                {step.enter ? new Date(step.enter).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </CollapsibleSection>

              {/* SECTION 3: Clinical Profile */}
              {(data.clinical_profile?.cpg_classification || data.clinical_profile?.pre_test_nprs) && (
                <CollapsibleSection id="clinical" title="🩺 Clinical Profile" defaultExpanded={false}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pain Zones</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.selected_pain_zones?.join(', ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">CPG Classification</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.cpg_classification || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pain Subtype</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.probable_subtype?.replace(/_/g, ' ') || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Pre-Test NPRS</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.pre_test_nprs ? `${data.clinical_profile.pre_test_nprs}/10` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Symptom Duration</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.clinical_profile?.symptom_duration || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Primary Goal</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {formatGoals(data.clinical_profile?.primary_goal)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Floor Capability</p>
                      <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                        {data.clinical_profile?.floor_capability || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Balance</p>
                      <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                        {data.clinical_profile?.balance || '—'}
                      </p>
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 4: Movement Tests */}
              {(data.movement_tests?.test1?.name || data.movement_tests?.test2?.name) && (
                <CollapsibleSection id="tests" title="🏃 Movement Tests" defaultExpanded={false}>
                  <div className="space-y-4">

                    {/* ---------------- TEST 1 — full dashboard ---------------- */}
                    {data.movement_tests?.test1?.name && (() => {
                      const test = data.movement_tests.test1

                      // ---- Step taxonomies ----------------------------------
                      const NC_STEPS = [
                        { id: "step1_intro", num: 1, name: "Intro" },
                        { id: "step2_device_placement", num: 2, name: "Device placement" },
                        { id: "step3_ready_prompt", num: 3, name: "Ready prompt" },
                        { id: "step4_countdown", num: 4, name: "Countdown" },
                        { id: "step5_reps", num: 5, name: "Reps" },
                      ]
                      const CAM_STAGES = [
                        { id: "step1_announcement", num: 1, name: "Announcement" },
                        { id: "step2_device_placement", num: 2, name: "Device placement" },
                        { id: "step3_controls", num: 3, name: "Controls" },
                        { id: "step4_get_visible", num: 4, name: "Get visible" },
                        { id: "step5_starting_position", num: 5, name: "Start position" },
                        { id: "step6_countdown", num: 6, name: "Countdown" },
                        { id: "__cam_reps", num: 7, name: "Reps" },
                      ]
                      const NC_COLOR = "#534AB7", NC_BG = "#EEEDFE", NC_TEXT = "#26215C"
                      const CAM_COLOR = "#1D9E75", CAM_BG = "#E1F5EE", CAM_TEXT = "#04342C"
                      const NC_TINT = `linear-gradient(to right, ${NC_BG} 0%, transparent 60%)`
                      const CAM_TINT = `linear-gradient(to right, ${CAM_BG} 0%, transparent 60%)`

                      // ---- Helpers ------------------------------------------
                      const fmtTime = (d) => d.toISOString().substr(11, 8)
                      const fmtDur = (ms) => {
                        if (ms == null || isNaN(ms)) return "—"
                        if (ms < 1000) return `${ms}ms`
                        const s = Math.round(ms / 1000)
                        if (s < 60) return `${s}s`
                        const m = Math.floor(s / 60), rs = s % 60
                        return rs ? `${m}m ${rs}s` : `${m}m`
                      }

                      // ---- Normalize event stream ---------------------------
                      // Supports both [name, payload] tuples AND flat objects.
                      // Field can be `testEvents` (raw) or `events` (flattened).
                      const rawEvents = test.testEvents || test.events || []
                      const events = rawEvents.map((entry) => {
                        if (Array.isArray(entry) && entry.length === 2) {
                          const [name, payload] = entry
                          const ts = payload?.event_timestamp
                          return { name, timestamp: ts ? new Date(ts) : null, ...payload }
                        }
                        if (entry && typeof entry === "object") {
                          const name = entry.name || entry.event_name
                          const ts = entry.event_timestamp || entry.timestamp
                          return { ...entry, name, timestamp: ts ? new Date(ts) : null }
                        }
                        return null
                      }).filter((e) => e && e.timestamp)

                      // ---- Derive insights ----------------------------------
                      let ins = null
                      if (events.length) {
                        const start = events[0].timestamp
                        const end = events[events.length - 1].timestamp
                        let currentMode = events[0].camera_mode || "no-camera"
                        const modeSegments = [{ mode: currentMode, start, durationMs: 0 }]
                        const ncStepsReached = new Set()
                        const camStepsReached = new Set()
                        let furthestNcStep = null, furthestCamStage = null
                        let maxCamRepCount = 0, maxNcRepCount = 0
                        let bgStart = null, totalBgMs = 0
                        const modeSwitches = []

                        for (const e of events) {
                          if (e.name === "switch_to_camera_tapped" && currentMode !== "camera") {
                            modeSegments[modeSegments.length - 1].durationMs = e.timestamp - modeSegments[modeSegments.length - 1].start
                            modeSwitches.push({ from: "no-camera", to: "camera", stage_at_switch: e.stage_at_switch || null, index: modeSwitches.length + 1, timestamp: e.timestamp })
                            currentMode = "camera"
                            modeSegments.push({ mode: currentMode, start: e.timestamp, durationMs: 0 })
                          } else if (e.name === "switch_to_no_camera_tapped" && currentMode !== "no-camera") {
                            const dwell = e.timestamp - modeSegments[modeSegments.length - 1].start
                            modeSegments[modeSegments.length - 1].durationMs = dwell
                            modeSwitches.push({ from: "camera", to: "no-camera", stage_at_switch: e.stage_at_switch || null, dwellMs: dwell, index: modeSwitches.length + 1, timestamp: e.timestamp })
                            currentMode = "no-camera"
                            modeSegments.push({ mode: currentMode, start: e.timestamp, durationMs: 0 })
                          }
                          if (e.name === "step_changed" && e.step) {
                            if (e.camera_mode === "camera") {
                              const s = CAM_STAGES.find((x) => x.id === e.step)
                              if (s) { camStepsReached.add(s.id); if (!furthestCamStage || s.num > furthestCamStage.num) furthestCamStage = s }
                            } else {
                              const s = NC_STEPS.find((x) => x.id === e.step)
                              if (s) { ncStepsReached.add(s.id); if (!furthestNcStep || s.num > furthestNcStep.num) furthestNcStep = s }
                            }
                          }
                          if (e.current_step) {
                            const s = CAM_STAGES.find((x) => x.id === e.current_step)
                            if (s) { camStepsReached.add(s.id); if (!furthestCamStage || s.num > furthestCamStage.num) furthestCamStage = s }
                          }
                          const isCamRep = e.context_type === "rom_test1" || e.camera_mode === "camera"
                          if (e.name === "rep_completed" && typeof e.rep_number === "number") {
                            if (isCamRep) { if (e.rep_number > maxCamRepCount) maxCamRepCount = e.rep_number }
                            else { if (e.rep_number > maxNcRepCount) maxNcRepCount = e.rep_number }
                          }
                          if (e.name === "rep_started") {
                            if (isCamRep) {
                              const s = CAM_STAGES.find((x) => x.id === "__cam_reps")
                              camStepsReached.add(s.id)
                              if (!furthestCamStage || s.num > furthestCamStage.num) furthestCamStage = s
                            } else {
                              const s = NC_STEPS.find((x) => x.id === "step5_reps")
                              ncStepsReached.add(s.id)
                              if (!furthestNcStep || s.num > furthestNcStep.num) furthestNcStep = s
                            }
                          }
                          if (typeof e.test1_rep_count === "number" && e.test1_rep_count > maxNcRepCount) maxNcRepCount = e.test1_rep_count
                          if (e.name === "app_backgrounded") bgStart = e.timestamp
                          if (e.name === "app_foregrounded" && bgStart) { totalBgMs += e.timestamp - bgStart; bgStart = null }
                        }
                        modeSegments[modeSegments.length - 1].durationMs = end - modeSegments[modeSegments.length - 1].start
                        const maxRepCount = Math.max(maxNcRepCount, maxCamRepCount)
                        const hasSkip = events.some((e) => e.name === "skip_exercise_pressed")
                        const hasClose = events.some((e) => e.name === "close_button_tapped")
                        let outcomeClass = "partial"
                        if (maxRepCount >= 10) outcomeClass = "complete"
                        else if (hasClose || hasSkip) outcomeClass = "dropoff"
                        let lastReached
                        if (maxRepCount > 0) lastReached = `${maxRepCount} of 10 reps completed`
                        else if (furthestNcStep) lastReached = `reached NC step ${furthestNcStep.num} (${furthestNcStep.name}) without completing any reps`
                        else if (furthestCamStage) lastReached = `reached camera flow (${furthestCamStage.name}) without progressing`
                        else lastReached = "did not progress past entry"
                        let narrative
                        if (outcomeClass === "complete") narrative = "User completed all 10 reps."
                        else if (outcomeClass === "dropoff") {
                          const parts = [`User ${lastReached}.`]
                          if (modeSwitches.length > 0) parts.push(`${modeSwitches.length} mode switch${modeSwitches.length > 1 ? "es" : ""} during the session.`)
                          if (totalBgMs > 30000) parts.push(`App was backgrounded for ${fmtDur(totalBgMs)} before final exit.`)
                          narrative = parts.join(" ")
                        } else narrative = `User ${lastReached}.`
                        ins = { modeSegments, modeSwitches, ncStepsReached, camStepsReached, furthestNcStep, furthestCamStage, maxRepCount, maxCamRepCount, maxNcRepCount, totalBgMs, outcomeClass, narrative, eventCount: events.length }
                      }

                      // ---- Build timeline rows ------------------------------
                      const buildRows = () => {
                        const rows = []
                        if (!events.length) return rows
                        let currentMode = events[0].camera_mode || "no-camera"
                        let lastBandMode = null, bgStartIdx = null, switchCounter = 0
                        let lastSwitchTapAt = null, lastSwitchToMode = null, sawPostTap = false
                        const pushBand = (mode, meta = "") => {
                          if (mode !== lastBandMode) { rows.push({ kind: "band", mode, meta }); lastBandMode = mode }
                        }
                        pushBand(currentMode, "starting flow")
                        for (let i = 0; i < events.length; i++) {
                          const e = events[i]
                          if (e.name === "app_backgrounded") { bgStartIdx = i; continue }
                          if (e.name === "app_foregrounded" && bgStartIdx !== null) {
                            rows.push({ kind: "gap", mode: currentMode, gapMs: events[bgStartIdx].timestamp - e.timestamp })
                            bgStartIdx = null
                            continue
                          }
                          if (e.name === "switch_to_camera_tapped") {
                            switchCounter++; lastSwitchTapAt = e.timestamp; lastSwitchToMode = "camera"; sawPostTap = false
                            rows.push({ kind: "switch", e, from: "no-camera", to: "camera", index: switchCounter })
                            currentMode = "camera"; pushBand(currentMode, ""); continue
                          }
                          if (e.name === "switch_to_no_camera_tapped") {
                            switchCounter++; lastSwitchTapAt = e.timestamp; lastSwitchToMode = "no-camera"; sawPostTap = false
                            let dwellMs = null
                            for (let j = i - 1; j >= 0; j--) { if (events[j].name === "switch_to_camera_tapped") { dwellMs = e.timestamp - events[j].timestamp; break } }
                            rows.push({ kind: "switch", e, from: "camera", to: "no-camera", index: switchCounter, dwellMs })
                            currentMode = "no-camera"; pushBand(currentMode, "resumed"); continue
                          }
                          let inferredFlip = false
                          if (e.camera_mode && e.camera_mode !== currentMode && e.name !== "switch_to_camera_tapped" && e.name !== "switch_to_no_camera_tapped") {
                            const within = lastSwitchTapAt != null && (e.timestamp - lastSwitchTapAt) < 5000
                            const firstArrival = lastSwitchToMode != null && e.camera_mode === lastSwitchToMode && !sawPostTap
                            currentMode = e.camera_mode
                            if (within || firstArrival) pushBand(currentMode)
                            else { inferredFlip = true; pushBand(currentMode, "silent flip (no tap recorded)") }
                          }
                          if (lastSwitchToMode != null && e.camera_mode === lastSwitchToMode) sawPostTap = true
                          rows.push({ kind: "event", e, mode: currentMode, inferredFlip })
                        }
                        if (bgStartIdx !== null) rows.push({ kind: "gap", mode: currentMode, gapMs: null })
                        return rows
                      }
                      const timelineRows = buildRows()

                      // ---- Per-event rendering helper -----------------------
                      const renderEventRow = (row, key) => {
                        const { e, mode, inferredFlip } = row
                        const palette = mode === "camera" ? { color: CAM_COLOR, bg: CAM_BG, text: CAM_TEXT } : { color: NC_COLOR, bg: NC_BG, text: NC_TEXT }
                        const tint = mode === "camera" ? CAM_TINT : NC_TINT
                        let title = e.name, icon = "•", iconColor = "#94a3b8"
                        const badges = [], subParts = []
                        let isMyra = false
                        switch (e.name) {
                          case "movementTest1_entered":
                          case "movementTest2_entered":
                            title = `Entered ${e.name === "movementTest1_entered" ? "Movement Test 1" : "Movement Test 2"}`
                            icon = "▸"; iconColor = palette.color
                            if (e.camera_mode) badges.push(`${e.camera_mode === "camera" ? "Camera" : "NC"} mode`)
                            break
                          case "step_changed": {
                            const ncS = NC_STEPS.find((s) => s.id === e.step)
                            const camS = CAM_STAGES.find((s) => s.id === e.step)
                            if (ncS && e.camera_mode !== "camera") { title = `Advanced to Step ${ncS.num}`; badges.push(`NC · ${ncS.name}`) }
                            else if (camS) { title = `Camera stage: ${camS.name}`; badges.push(`Camera · ${camS.id}`) }
                            else title = `Step changed: ${e.step}`
                            icon = "▸"; iconColor = palette.color
                            break
                          }
                          case "myra_spoke": isMyra = true; break
                          case "video_playback_started":
                            title = "Demo video started"; icon = "▷"
                            if (e.video_url) { try { subParts.push(decodeURIComponent(e.video_url.split("/").pop() || "")) } catch {} }
                            break
                          case "camera_chip_tapped": title = "Tapped camera chip"; icon = "📷"; iconColor = "#BA7517"; break
                          case "camera_permission_granted": title = "Camera permission granted"; icon = "✓"; iconColor = "#1D9E75"; break
                          case "camera_permission_denied": title = "Camera permission denied"; icon = "✕"; iconColor = "#A32D2D"; break
                          case "countdown_completed": title = "Countdown completed"; icon = "⏱"; break
                          case "exercise_started": title = "Exercise started"; icon = "▶"; iconColor = "#1D9E75"; break
                          case "exercise_restarted":
                            title = "Exercise restarted"; icon = "↻"; iconColor = "#BA7517"
                            if (e.restarted_at_rep != null) subParts.push(`Restarted at rep ${e.restarted_at_rep}`)
                            break
                          case "rep_started": title = `Rep ${e.rep_number ?? "?"} started`; iconColor = palette.color; break
                          case "rep_completed":
                            title = `Rep ${e.rep_number ?? "?"} completed`; iconColor = palette.color
                            if (e.rep_duration_ms != null) badges.push(`${e.rep_duration_ms}ms${e.rep_duration_ms < 100 ? " ⚠" : ""}`)
                            if (e.inter_rep_gap_ms != null) subParts.push(`gap: ${fmtDur(e.inter_rep_gap_ms)}`)
                            break
                          case "completed": title = "Test completed"; icon = "✓"; iconColor = "#1D9E75"; break
                          case "skip_exercise_pressed": title = "User skipped exercise"; icon = "⤳"; iconColor = "#BA7517"; break
                          case "close_button_tapped": title = "User closed session"; icon = "✕"; iconColor = "#A32D2D"; break
                          case "user_input_received":
                            title = "User input"; icon = "⌨"
                            if (e.input_text) subParts.push(`"${e.input_text}" (${e.input_mode})`)
                            break
                          case "ai_image_check_sent": title = "AI image check sent"; icon = "🔍"; if (e.check_type) badges.push(e.check_type); break
                          case "gsa_gate1_evaluated":
                          case "gsa_gate2_evaluated":
                          case "gsa_gate3_evaluated": {
                            const gate = e.name.replace("gsa_gate", "Gate ").replace("_evaluated", "")
                            title = `${gate} evaluated`
                            icon = e.result === "pass" ? "✓" : "✕"
                            iconColor = e.result === "pass" ? "#1D9E75" : "#A32D2D"
                            badges.push(e.result)
                            if (e.failure_reason) subParts.push(`reason: ${e.failure_reason}`)
                            break
                          }
                          default: title = e.name.replace(/_/g, " ")
                        }
                        if (isMyra) {
                          const sub = `${e.message_id ? e.message_id + " · " : ""}trigger: ${e.message_trigger || "unknown"}`
                          return (
                            <div key={key} className="grid items-start gap-3 py-3 border-b border-slate-100"
                              style={{ gridTemplateColumns: "72px 4px 18px 1fr", background: tint }}>
                              <div className="text-[12px] font-mono text-slate-500 pt-0.5">{fmtTime(e.timestamp)}</div>
                              <div className="self-stretch min-h-[26px]" style={{ background: palette.color }} />
                              <div className="text-center pt-1" style={{ color: palette.color }}>💬</div>
                              <div>
                                <div className="text-xs text-slate-500 mb-1">Myra · <span className="text-slate-400">{sub}</span></div>
                                <div className="text-sm rounded-md px-3 py-2 inline-block" style={{ background: palette.bg, color: palette.text }}>
                                  "{e.message_text || ""}"
                                </div>
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div key={key} className="grid items-start gap-3 py-3 border-b border-slate-100"
                            style={{ gridTemplateColumns: "72px 4px 18px 1fr", background: tint, boxShadow: inferredFlip ? "inset 3px 0 0 #BA7517" : undefined }}>
                            <div className="text-[12px] font-mono text-slate-500 pt-0.5">{fmtTime(e.timestamp)}</div>
                            <div className="self-stretch min-h-[26px]" style={{ background: palette.color }} />
                            <div className="text-center pt-1 text-sm" style={{ color: iconColor }}>{icon}</div>
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-slate-800">{title}</span>
                                {badges.map((b, i) => (
                                  <span key={i} className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-600">{b}</span>
                                ))}
                                {inferredFlip && (
                                  <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-700"
                                    title="camera_mode field changed without a switch_to_*_tapped event">⚠ silent mode flip</span>
                                )}
                              </div>
                              {subParts.length > 0 && <div className="text-xs text-slate-500 mt-1">{subParts.join(" · ")}</div>}
                            </div>
                          </div>
                        )
                      }

                      // ---- Apply filter (state lives in parent component) ---
                      const activeFilter = currentFilter
                      const setFilter = setCurrentFilter
                      const KEY_EVENTS = new Set([
                        "movementTest1_entered", "step_changed", "exercise_started", "exercise_restarted",
                        "countdown_completed", "completed", "camera_chip_tapped", "switch_to_camera_tapped",
                        "switch_to_no_camera_tapped", "skip_exercise_pressed", "close_button_tapped",
                      ])
                      const matchFilter = (row, f) => {
                        if (f === "all") return true
                        if (row.kind === "band" || row.kind === "gap") return true
                        if (row.kind === "switch") return f === "switch" || f === "key" || f === "user"
                        if (row.kind === "event") {
                          const n = row.e.name
                          if (f === "myra") return n === "myra_spoke"
                          if (f === "key") return KEY_EVENTS.has(n) || n === "user_input_received"
                          if (f === "user") return n === "user_input_received" || n === "camera_chip_tapped" || n === "skip_exercise_pressed" || n === "close_button_tapped"
                          if (f === "switch") return false
                        }
                        return false
                      }
                      const visibleRaw = timelineRows.filter((r) => matchFilter(r, activeFilter))
                      const cleanedRows = []
                      for (let i = 0; i < visibleRaw.length; i++) {
                        const cur = visibleRaw[i]
                        if (cur.kind === "band") {
                          const next = visibleRaw[i + 1]
                          if (!next || next.kind === "band") continue
                        }
                        cleanedRows.push(cur)
                      }

                      // ---- Mode legend --------------------------------------
                      let ncMs = 0, camMs = 0
                      if (ins) for (const seg of ins.modeSegments) { if (seg.mode === "camera") camMs += seg.durationMs; else ncMs += seg.durationMs }
                      const totalMs = ncMs + camMs
                      const ncPct = totalMs > 0 ? Math.round((ncMs / totalMs) * 100) : 0
                      const camPct = totalMs > 0 ? Math.round((camMs / totalMs) * 100) : 0
                      const showLegend = ins && (camMs > 0 || ins.modeSwitches.length > 0)
                      const enteredCamera = ins && (ins.modeSwitches.some((s) => s.to === "camera") || ins.camStepsReached.size > 0 || ins.maxCamRepCount > 0)

                      const responseColors = {
                        IMPROVED: "text-green-700 bg-green-50",
                        SAME: "text-amber-700 bg-amber-50",
                        WORSENED: "text-red-700 bg-red-50",
                      }
                      const bannerCls = ins?.outcomeClass === "complete" ? "bg-green-50 text-green-800 border-green-200"
                        : ins?.outcomeClass === "dropoff" ? "bg-amber-50 text-amber-800 border-amber-200"
                        : "bg-slate-50 text-slate-700 border-slate-200"

                      return (
                        <div className="border border-slate-200 rounded-lg p-4 space-y-5">
                          {/* Header */}
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-slate-700">Test 1: {test.name}</h4>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${test.completed ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                              {test.completed ? "Completed" : "Incomplete"}
                            </span>
                          </div>

                          {/* Existing summary fields */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-slate-500">Symptom Response</p>
                              <p className={`text-sm font-medium mt-1 px-2 py-0.5 rounded inline-block ${responseColors[test.response] || "text-slate-600 bg-slate-50"}`}>
                                {test.response || "—"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">RPE</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">{test.rpe != null ? `${test.rpe}/10` : "—"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Video Watched</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">{test.video_watched_fully ? "Yes" : "No"}</p>
                            </div>
                            {test.exit_reason && (
                              <div>
                                <p className="text-xs text-slate-500">Exit Reason</p>
                                <p className="text-sm font-medium text-red-600 mt-1">{formatExitReason(test.exit_reason)}</p>
                              </div>
                            )}
                          </div>

                          {!ins ? (
                            <p className="text-sm text-slate-500 italic">No events captured for this test.</p>
                          ) : (
                            <>
                              {/* Outcome banner */}
                              <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${bannerCls}`}>
                                <span className="text-lg leading-none">
                                  {ins.outcomeClass === "complete" ? "✓" : ins.outcomeClass === "dropoff" ? "⚠" : "ⓘ"}
                                </span>
                                <div className="text-sm">{ins.narrative}</div>
                              </div>

                              {/* Metrics strip */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <p className="text-xs text-slate-500 uppercase font-medium">Reps completed</p>
                                  <p className="text-xl font-semibold text-slate-800 mt-1">
                                    {enteredCamera && ins.maxNcRepCount > 0 ? Math.max(ins.maxNcRepCount, ins.maxCamRepCount)
                                      : enteredCamera ? ins.maxCamRepCount
                                      : ins.maxNcRepCount}
                                    <span className="text-base font-normal text-slate-400"> / 10</span>
                                  </p>
                                  {enteredCamera && ins.maxNcRepCount > 0 && (
                                    <p className="text-xs text-slate-500 mt-1">NC {ins.maxNcRepCount} · Cam {ins.maxCamRepCount}</p>
                                  )}
                                  {enteredCamera && ins.maxNcRepCount === 0 && (
                                    <p className="text-xs text-slate-500 mt-1">Camera flow</p>
                                  )}
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <p className="text-xs text-slate-500 uppercase font-medium">Furthest step</p>
                                  <p className="text-xl font-semibold text-slate-800 mt-1">
                                    {ins.furthestNcStep ? `NC · ${ins.furthestNcStep.num} / ${NC_STEPS.length}` : "NC · —"}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {ins.furthestCamStage ? `Cam · ${ins.furthestCamStage.num} / ${CAM_STAGES.length}` : "Cam · never visited"}
                                  </p>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <p className="text-xs text-slate-500 uppercase font-medium">Mode switches</p>
                                  <p className="text-xl font-semibold text-slate-800 mt-1">{ins.modeSwitches.length}</p>
                                  {ins.modeSwitches.length > 0 && (
                                    <p className="text-xs text-slate-500 mt-1">
                                      {ins.modeSwitches.map((s) => s.from === "no-camera" ? "NC" : "C")
                                        .concat([ins.modeSwitches[ins.modeSwitches.length - 1].to === "no-camera" ? "NC" : "C"])
                                        .join(" → ")}
                                    </p>
                                  )}
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                  <p className="text-xs text-slate-500 uppercase font-medium">Time backgrounded</p>
                                  <p className="text-xl font-semibold text-slate-800 mt-1">{fmtDur(ins.totalBgMs)}</p>
                                </div>
                              </div>

                              {/* Step progressions */}
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-2">Step progression (no-camera)</h4>
                                  <div className="grid grid-cols-5 gap-2">
                                    {NC_STEPS.map((step) => {
                                      const reached = ins.ncStepsReached.has(step.id)
                                      const isFurthest = ins.furthestNcStep && ins.furthestNcStep.id === step.id
                                      const stalled = isFurthest && step.id === "step5_reps" && ins.maxNcRepCount < 10
                                      const meta = !reached ? "— Not reached" : step.id === "step5_reps" ? (ins.maxNcRepCount >= 10 ? "✓ 10 / 10 reps" : `${ins.maxNcRepCount} / 10 reps`) : "✓ Reached"
                                      const cls = !reached ? "bg-slate-50 border-slate-200 text-slate-400" : stalled ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-green-50 border-green-200 text-green-800"
                                      return (
                                        <div key={step.id} className={`rounded-md border px-3 py-2 ${cls}`}>
                                          <div className="text-[10px] uppercase tracking-wider opacity-70">Step {step.num}</div>
                                          <div className="text-xs font-medium leading-tight mt-0.5">{step.name}</div>
                                          <div className="text-[10px] mt-1 opacity-80">{meta}</div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                                {enteredCamera && (
                                  <div>
                                    <h4 className="text-xs uppercase tracking-wider text-teal-700 font-semibold mb-2">Step progression (camera)</h4>
                                    <div className="grid grid-cols-4 lg:grid-cols-7 gap-2">
                                      {CAM_STAGES.map((step) => {
                                        const reached = ins.camStepsReached.has(step.id)
                                        const isFurthest = ins.furthestCamStage && ins.furthestCamStage.id === step.id
                                        const stalled = isFurthest && step.id === "__cam_reps" && ins.maxCamRepCount < 10
                                        const meta = !reached ? "— Not reached" : step.id === "__cam_reps" ? (ins.maxCamRepCount >= 10 ? "✓ 10 / 10 reps" : `${ins.maxCamRepCount} / 10 reps`) : "✓ Reached"
                                        const cls = !reached ? "bg-slate-50 border-slate-200 text-slate-400" : stalled ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-green-50 border-green-200 text-green-800"
                                        return (
                                          <div key={step.id} className={`rounded-md border px-3 py-2 ${cls}`}>
                                            <div className="text-[10px] uppercase tracking-wider opacity-70">Step {step.num}</div>
                                            <div className="text-xs font-medium leading-tight mt-0.5">{step.name}</div>
                                            <div className="text-[10px] mt-1 opacity-80">{meta}</div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Timeline */}
                              <div>
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                  <h4 className="text-xs uppercase tracking-wider text-teal-700 font-semibold">Timeline · {ins.eventCount} events</h4>
                                  {showLegend && (
                                    <div className="inline-flex items-center gap-3 px-3 py-1 bg-slate-100 rounded-full text-xs text-slate-700">
                                      <span>
                                        <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: NC_COLOR }} />
                                        No-camera {fmtDur(ncMs)} <span className="text-slate-400">· {ncPct}%</span>
                                      </span>
                                      <span>
                                        <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: CAM_COLOR }} />
                                        Camera {fmtDur(camMs)} <span className="text-slate-400">· {camPct}%</span>
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-1">
                                    {[
                                      { id: "all", label: "All" },
                                      { id: "key", label: "Key moments" },
                                      { id: "myra", label: "Myra messages" },
                                      { id: "user", label: "User actions" },
                                      { id: "switch", label: "Mode switches" },
                                    ].map((b) => (
                                      <button key={b.id} onClick={() => setFilter(b.id)}
                                        className={`px-3 py-1 text-xs rounded-full transition-colors ${activeFilter === b.id ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                                        {b.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div className="border border-slate-200 rounded-lg overflow-hidden p-4">
                                  {cleanedRows.length === 0 ? (
                                    <div className="text-sm text-slate-500">No events match this filter.</div>
                                  ) : (
                                    cleanedRows.map((row, i) => {
                                      if (row.kind === "band") {
                                        const palette = row.mode === "camera" ? { color: CAM_COLOR } : { color: NC_COLOR }
                                        return (
                                          <div key={i} className="grid items-center gap-3 py-3 pt-4 border-t border-slate-200 first:border-t-0 first:pt-0"
                                            style={{ gridTemplateColumns: "72px 4px 18px 1fr", background: row.mode === "camera" ? CAM_TINT : NC_TINT }}>
                                            <div />
                                            <div className="self-stretch min-h-[20px]" style={{ background: palette.color }} />
                                            <div />
                                            <div className="flex items-center gap-2">
                                              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: palette.color }}>
                                                {row.mode === "camera" ? "📷 Camera mode" : "📱 No-camera mode"}
                                              </span>
                                              {row.meta && <span className="text-[11px] text-slate-400">· {row.meta}</span>}
                                            </div>
                                          </div>
                                        )
                                      }
                                      if (row.kind === "gap") {
                                        const label = row.gapMs ? `App backgrounded for ${fmtDur(Math.abs(row.gapMs))}` : "App backgrounded (no return before session end)"
                                        return (
                                          <div key={i} className="grid items-center gap-3 py-2"
                                            style={{ gridTemplateColumns: "72px 4px 18px 1fr", background: row.mode === "camera" ? CAM_TINT : NC_TINT }}>
                                            <div />
                                            <div className="self-stretch min-h-[16px]"
                                              style={{ backgroundImage: "repeating-linear-gradient(to bottom, #cbd5e1 0 4px, transparent 4px 8px)" }} />
                                            <div className="text-slate-300 text-center">⋮</div>
                                            <div className="text-xs text-slate-500 italic">{label}</div>
                                          </div>
                                        )
                                      }
                                      if (row.kind === "switch") {
                                        const { e, from, to, index, dwellMs } = row
                                        const fromColor = from === "no-camera" ? NC_COLOR : CAM_COLOR
                                        const toColor = to === "no-camera" ? NC_COLOR : CAM_COLOR
                                        return (
                                          <div key={i} className="grid items-start gap-3 py-3 border-b border-slate-100"
                                            style={{ gridTemplateColumns: "72px 4px 18px 1fr" }}>
                                            <div className="text-[12px] font-mono text-slate-500 pt-0.5">{fmtTime(e.timestamp)}</div>
                                            <div className="self-stretch min-h-[26px]"
                                              style={{ background: `linear-gradient(to bottom, ${fromColor} 0%, ${fromColor} 30%, ${toColor} 70%, ${toColor} 100%)` }} />
                                            <div className="text-amber-600 text-center pt-1">⇅</div>
                                            <div>
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm font-medium text-slate-800">
                                                  Mode switch #{index}: {from === "no-camera" ? "No-camera" : "Camera"} → {to === "no-camera" ? "No-camera" : "Camera"}
                                                </span>
                                                {dwellMs != null && (
                                                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${dwellMs < 10000 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                                    {fmtDur(dwellMs)} dwell
                                                  </span>
                                                )}
                                              </div>
                                              {e.stage_at_switch && (
                                                <div className="text-xs text-slate-500 mt-1">
                                                  Stage at switch: <code className="text-slate-700">{e.stage_at_switch}</code>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      }
                                      return renderEventRow(row, i)
                                    })
                                  )}
                                </div>
                              </div>
                            </>
                          )}

                          {renderROMChart(test.rom_data)}
                        </div>
                      )
                    })()}

                    {/* ---------------- TEST 2 — original summary card ---------------- */}
                    {data.movement_tests?.test2?.name && (() => {
                      const test = data.movement_tests.test2
                      const responseColors = {
                        IMPROVED: 'text-green-600 bg-green-50',
                        SAME: 'text-amber-600 bg-amber-50',
                        WORSENED: 'text-red-600 bg-red-50'
                      }
                      return (
                        <div className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-700">Test 2: {test.name}</h4>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              test.completed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {test.completed ? 'Completed' : 'Incomplete'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-slate-500">Symptom Response</p>
                              <p className={`text-sm font-medium mt-1 px-2 py-0.5 rounded inline-block ${
                                responseColors[test.response] || 'text-slate-600 bg-slate-50'
                              }`}>
                                {test.response || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">RPE</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">
                                {test.rpe !== null && test.rpe !== undefined ? `${test.rpe}/10` : '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Video Watched</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">
                                {test.video_watched_fully ? 'Yes' : 'No'}
                              </p>
                            </div>
                            {test.exit_reason && (
                              <div>
                                <p className="text-xs text-slate-500">Exit Reason</p>
                                <p className="text-sm font-medium text-red-600 mt-1">
                                  {formatExitReason(test.exit_reason)}
                                </p>
                              </div>
                            )}
                          </div>
                          {renderROMChart(test.rom_data)}
                        </div>
                      )
                    })()}

                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 5: Demographics */}
              {(data.demographics?.age || data.demographics?.weight_lbs || data.demographics?.activity_level) && (
                <CollapsibleSection id="demographics" title="👤 Demographics" defaultExpanded={false}>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Age</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">{data.demographics?.age || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Weight</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.demographics?.weight_lbs ? `${data.demographics.weight_lbs} lbs` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Activity Level</p>
                      <p className="text-sm font-medium text-slate-800 mt-1">
                        {data.demographics?.activity_level?.replace(/_/g, ' ') || '—'}
                      </p>
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 6: Recovery Plan */}
              {(data.recovery_plan?.final_recovery_score || data.recovery_plan?.plan_rationale_speech) && (
                <CollapsibleSection id="recovery" title="📊 Recovery Plan" defaultExpanded={false}>
                  <div className="space-y-4">
                    {data.recovery_plan?.final_recovery_score && (
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                            <span className="text-xl font-bold text-white">{data.recovery_plan.final_recovery_score}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Recovery Score</p>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-600">
                            This score represents the baseline assessment of movement capability and pain sensitivity.
                          </p>
                        </div>
                      </div>
                    )}
                    {data.recovery_plan?.plan_rationale_speech && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-xs text-slate-500 uppercase mb-2">Myra's Plan Rationale</p>
                        <p className="text-sm text-slate-700 italic">"{data.recovery_plan.plan_rationale_speech}"</p>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 7: Myra Conversations */}
              <CollapsibleSection id="chats" title="💬 Myra Conversations" defaultExpanded={true}>
                {data.chats && Object.keys(data.chats).length > 0 ? (
                  ['landing', 'post_plan', 'post_session', 'gsa_mid_journey'].filter(tp => data.chats[tp]?.length > 0).map(tp => {
                    const msgs = data.chats[tp]
                    const conversations = []
                    const indexById = new Map()
                    msgs.forEach((msg) => {
                      const key = msg.conversation_id || 'unknown'
                      if (!indexById.has(key)) {
                        indexById.set(key, conversations.length)
                        conversations.push({ conversation_id: msg.conversation_id, messages: [] })
                      }
                      conversations[indexById.get(key)].messages.push(msg)
                    })

                    const questionCount = msgs.filter(m => m.role === 'user').length

                    return (
                      <div key={tp} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full bg-purple-500" />
                          <span className="text-xs font-semibold text-purple-700 uppercase">{TOUCHPOINT_LABELS[tp]}</span>
                          <span className="text-xs text-slate-400">{questionCount} question{questionCount !== 1 ? 's' : ''}</span>
                          {conversations.length > 1 && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              {conversations.length} separate attempts
                            </span>
                          )}
                        </div>

                        <div className="space-y-4">
                          {conversations.map((convo, cIdx) => (
                            <div key={convo.conversation_id || cIdx} className="pl-4 border-l-2 border-purple-100">
                              {conversations.length > 1 && (
                                <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                                  Attempt {cIdx + 1}
                                  {convo.messages[0]?.created_at && (
                                    <span className="ml-2 font-normal normal-case text-slate-400">
                                      {formatDateTime(convo.messages[0].created_at)}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="space-y-2">
                                {convo.messages.map((msg, idx) => (
                                  <div
                                    key={idx}
                                    className={`rounded-lg p-3 ${
                                      msg.role === 'user'
                                        ? 'bg-purple-50 border border-purple-100'
                                        : 'bg-slate-50 border border-slate-100'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                                        {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                                      </span>
                                      {msg.input_method && msg.role === 'user' && (
                                        <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">{msg.input_method}</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-700">{msg.content}</p>
                                    {msg.intent && msg.role === 'assistant' && (
                                      <p className="text-xs text-slate-400 mt-1">Intent: {msg.intent}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">No Myra conversations for this session.</p>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


// =============================================================================
// APP TAB - USER DETAIL MODAL COMPONENT
// =============================================================================
const AppUserDetailModal = ({ isOpen, onClose, user, gsaData, sessions, loading, onSessionClick }) => {
  if (!isOpen) return null

  // Status badge helper
  const getStatusBadge = (status) => {
    const mapping = {
      'F': { label: 'Free', className: 'bg-green-100 text-green-700' },
      'P': { label: 'Paid', className: 'bg-blue-100 text-blue-700' },
      'E': { label: 'Expired', className: 'bg-red-100 text-red-700' }
    }
    return mapping[status] || { label: status, className: 'bg-gray-100 text-gray-700' }
  }

  const statusBadge = getStatusBadge(user?.status)

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[55]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-blue-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">User Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm font-medium text-blue-700">{user?.name || 'Unknown'}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">{user?.email}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">ID: {user?.user_id}</span>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading user details...</div>
          ) : (
            <div className="space-y-0">
              {/* SECTION 1: GSA Overview */}
              <CollapsibleSection id="gsa-overview" title="📋 GSA Overview" defaultExpanded={true}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">GSA Date</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">{formatDate(gsaData?.gsa_date)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Device</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{gsaData?.device_type || '—'}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Location</p>
                    <p className="text-sm font-semibold text-slate-800 mt-1">
                      {[gsaData?.city, gsaData?.state].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase font-medium">Recovery Score</p>
                    {gsaData?.recovery_score ? (
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                          <span className="text-sm font-bold text-white">{gsaData.recovery_score}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 mt-1">—</p>
                    )}
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION 2: Clinical Profile */}
              <CollapsibleSection id="clinical-profile" title="🩺 Clinical Profile" defaultExpanded={true}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pain Zones</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.pain_zones?.join(', ') || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">CPG Classification</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.cpg_classification || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pain Subtype</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.pain_subtype?.replace(/_/g, ' ') || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Pre-Test NPRS</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.pre_test_nprs ? `${gsaData.pre_test_nprs}/10` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Symptom Duration</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.symptom_duration || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Primary Goal</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">
                      {gsaData?.primary_goal || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Floor Capability</p>
                    <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                      {gsaData?.floor_capability || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Balance</p>
                    <p className="text-sm font-medium text-slate-800 mt-1 capitalize">
                      {gsaData?.balance || '—'}
                    </p>
                  </div>
                </div>
              </CollapsibleSection>

              {/* SECTION 3: GSA Movement Tests */}
              {(gsaData?.test1 || gsaData?.test2) && (
                <CollapsibleSection id="gsa-tests" title="🏃 GSA Movement Tests" defaultExpanded={false}>
                  <div className="space-y-4">
                    {[gsaData?.test1, gsaData?.test2].map((test, idx) => {
                      if (!test?.name) return null
                      const responseColors = {
                        IMPROVED: 'text-green-600 bg-green-50',
                        SAME: 'text-amber-600 bg-amber-50',
                        WORSENED: 'text-red-600 bg-red-50'
                      }
                      return (
                        <div key={idx} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-slate-700">Test {idx + 1}: {test.name}</h4>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              test.completed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {test.completed ? 'Completed' : 'Incomplete'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-slate-500">Symptom Response</p>
                              <p className={`text-sm font-medium mt-1 px-2 py-0.5 rounded inline-block ${
                                responseColors[test.symptom_response] || 'text-slate-600 bg-slate-50'
                              }`}>
                                {test.symptom_response || '—'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">RPE</p>
                              <p className="text-sm font-medium text-slate-800 mt-1">
                                {test.rpe !== null && test.rpe !== undefined ? `${test.rpe}/10` : '—'}
                              </p>
                            </div>
                          </div>
                          {test.rom_data && test.rom_data.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-slate-500 mb-2">ROM by Rep ({test.rom_data.length} reps)</p>
                              <div className="flex gap-1 items-end h-8">
                                {test.rom_data.map((r, i) => (
                                  <div
                                    key={i}
                                    className="flex-1 bg-gradient-to-t from-teal-600 to-teal-400 rounded-sm"
                                    style={{ height: `${r}%` }}
                                    title={`Rep ${i + 1}: ${r}%`}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 4: Recovery Plan Rationale */}
              {gsaData?.plan_rationale_speech && (
                <CollapsibleSection id="plan-rationale" title="💬 Recovery Plan Rationale" defaultExpanded={false}>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-700 italic">"{gsaData.plan_rationale_speech}"</p>
                  </div>
                </CollapsibleSection>
              )}

              {/* SECTION 5: Exercise Sessions */}
              <CollapsibleSection 
                id="exercise-sessions" 
                title={`🏋️ Exercise Sessions (${sessions?.length || 0} total)`} 
                defaultExpanded={true}
              >
                {sessions && sessions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Planned Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Actual Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sets Target</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Sets Actual</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Progressions</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Regressions</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Questions</th>
                          {/* <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Last Login</th> */}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {sessions.map((session, idx) => (
                          <tr 
                            key={session.session_id || idx} 
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => onSessionClick(session)}
                          >
                            <td className="px-3 py-2 text-slate-600">{formatDate(session.planned_date)}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {session.actual_date ? formatDate(session.actual_date) : <span className="text-amber-500">Pending</span>}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{session.sets_target || '—'}</td>
                            <td className={`px-3 py-2 ${session.sets_actual < session.sets_target ? 'text-amber-600' : 'text-teal-600'}`}>
                              {session.sets_actual ?? '—'}
                            </td>
                            <td className="px-3 py-2">
                              {session.progressions > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">+{session.progressions}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {session.regressions > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">-{session.regressions}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {session.questions > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">{session.questions}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            {/* <td className="px-3 py-2 text-slate-600">
                              {session.last_login ? formatDateTime(session.last_login) : <span className="text-slate-400">—</span>}
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-3 text-center">Click a session row to view exercise details and post-session chat</p>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">No exercise sessions found.</p>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}
// =============================================================================
// APP TAB - GENERIC KPI DRILLDOWN MODAL (table style, matches GSA DrillDownModal)
// =============================================================================
const AppKpiDrilldownModal = ({ isOpen, onClose, title, rows, kind, onRowClick, onDownload, onNewTab,queryDateLabel }) => {
  if (!isOpen) return null

  const planLabel = (r) => {
    if (!r.product_name) return null;
    return r?.product_name;
  }

  const statusBadge = (r) => {
    if (r.subscription_status) {
      const s = r.subscription_status.toLowerCase()
      const cls =
        s === 'active' || s === 'trialing' ? 'bg-green-100 text-green-700' :
        s === 'past_due' || s === 'unpaid' ? 'bg-amber-100 text-amber-700' :
        'bg-red-100 text-red-700'
      return <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${cls}`}>{r.subscription_status}</span>
    }
    if (r.has_access !== undefined) {
      return r.has_access
        ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Active</span>
        : <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-500">No Access</span>
    }
    return <span className="text-slate-300">—</span>
  }

  const isSessionKind = kind === 'session'
  const isFreeSessionKind = kind === 'free_session_started' || kind === 'free_session_completed'
  const isNotSubscribedKind = kind === 'not_subscribed'
  const isSubscribedKind = kind === 'subscribed'
  const formatDiscount = (r) =>
    r.discount_percentage !== null && r.discount_percentage !== undefined
      ? `${Number(r.discount_percentage)}%`
      : null
  const formatMinutes = (mins) => {
    if (mins == null) return '—'
    if (mins < 60) return `${mins}m`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ${mins % 60}m`
    const days = Math.floor(hrs / 24)
    return `${days}d ${hrs % 24}h`
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[55]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
            <p className="text-sm text-slate-500 mt-0.5">{rows.length.toLocaleString()} {rows.length === 1 ? 'result' : 'results'}</p>
          </div>
          <div className="flex items-center gap-2">
            {onDownload && (
              <button
                onClick={() => onDownload(rows, title)}
                className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium"
              >
                ↓ CSV
              </button>
            )}
            {onNewTab && (
              <button
                onClick={() => onNewTab(rows, title)}
                className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium"
              >
                ↗ New Tab
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
          </div>
        </div>

        {/* Table */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {rows.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No results found.</div>
          ) : (
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Signup Date</th>
                  {isFreeSessionKind && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Time To Start Session</th>
                  )}
                  {/* {isFreeSessionKind && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      {kind === 'free_session_started' ? 'Time to Start Session' : 'Time to Complete Session'}
                    </th>
                  )} */}
                  {kind === 'free_session_started' && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Dropoff</th>
                  )}
                  {/* {isFreeSessionKind && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                      {kind === 'free_session_started' ? 'Time to Start Session' : 'Time to Complete Session'}
                    </th>
                  )} */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Skipped Subscription</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Feedback</th>
                  {(isSubscribedKind || isNotSubscribedKind) && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Discount</th>
                  )}
                  {isSessionKind && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Session</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                  {isSubscribedKind && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Paying</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Auto-Renew</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Free Session Started</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Free Session Completed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">GSA</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {rows.map((r, index) => {
                  const clickable = !!r.analytics_session_id && r.analytics_session_id !== "dev-team-skipped"
                  const dateValue = 
                    r.completed_date || r.session_completed_date || r.paid_through || r.signup_date || null
                  return (
                    <tr
                      key={r.user_id || r.program_task_id || index}
                      className={clickable ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-60'}
                      onClick={() => clickable && onRowClick(r)}
                    >
                     <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-800">
                        {r.first_name || r.name || '—'} {r.last_name || ''}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{r.email || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {r.signup_date ? formatDateTime(r.signup_date) : '—'}
                      </td>
                      {isFreeSessionKind && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-teal-700">
                          {formatMinutes(r.minutes_to_start ?? r.minutes_to_complete)}
                        </td>
                      )}
                      {kind === 'free_session_started' && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-amber-700">
                          {r.dropped_off_after
                            ? r.dropped_off_after.replace(/_/g, ' ')
                            : <span className="text-slate-300">—</span>}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {r.skipped_subscription
                          ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">Yes</span>
                          : <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-500">No</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 max-w-[240px] truncate" title={r.decline_feedback || r.feedback || ''}>
                        {(r.decline_feedback || r.feedback) || <span className="text-slate-300 italic">—</span>}
                      </td>
                      {(isSubscribedKind || isNotSubscribedKind) && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-teal-700">
                          {formatDiscount(r) || <span className="text-slate-300">—</span>}
                        </td>
                      )}
                      {isSessionKind && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{r.name || '—'}</td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {queryDateLabel || '—'}
                      </td>
                      {isSubscribedKind && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                          {r.final_amount_cents != null ? `$${(r.final_amount_cents / 100).toFixed(2)}` : '—'}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {planLabel(r) || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">{statusBadge(r)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {r.auto_renew === undefined || r.auto_renew === null
                          ? <span className="text-slate-300">—</span>
                          : r.auto_renew
                            ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-700">ON</span>
                            : <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-700">OFF</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {r.free_session_started_date ? formatDateTime(r.free_session_started_date) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {r.free_session_completed_date ? formatDateTime(r.free_session_completed_date) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {clickable
                          ? <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">View →</span>
                          : <span className="text-slate-300 text-xs">No GSA</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <span className="text-sm text-slate-500">
            Showing {rows.length.toLocaleString()} of {rows.length.toLocaleString()}
          </span>
          <button onClick={onClose} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors">
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}
const downloadAppKpiDrilldownJSON = (rows, title) => {
  if (!rows?.length) return
  const data = rows.map(r => ({
    name: `${r.first_name || r.name || ''} ${r.last_name || ''}`.trim(),
    email: r.email || '',
    session_name: r.name || '',
    signup_date: r.signup_date || '',
    completed_date: r.completed_date || r.session_completed_date || '',
    paid_through: r.paid_through || '',
    recurring_interval: r.recurring_interval || '',
    recurring_interval_count: r.recurring_interval_count || '',
    subscription_status: r.subscription_status || '',
    analytics_session_id: r.analytics_session_id || '',
  }))
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0,10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}

const openAppKpiDrilldownTab = (rows, title) => {
  if (!rows?.length) return
  const tableRows = rows.map(r => `
    <tr>
      <td>${r.first_name || r.name || ''} ${r.last_name || ''}</td>
      <td>${r.email || '—'}</td>
      <td>${formatDateTime(r.completed_date || r.session_completed_date || r.paid_through || r.signup_date) || '—'}</td>
      <td>${r.recurring_interval ? `${r.recurring_interval_count}× ${r.recurring_interval}` : '—'}</td>
      <td>${r.subscription_status || '—'}</td>
    </tr>`).join('')
  const html = `<!DOCTYPE html><html><head><title>${title}</title>
    <style>
      body { font-family: system-ui, sans-serif; padding: 24px; color: #1e293b; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      p { color: #64748b; font-size: 13px; margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th { background: #f8fafc; text-align: left; padding: 8px 12px; border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #94a3b8; }
      td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
      tr:hover td { background: #f8fafc; }
    </style></head><body>
    <h1>${title}</h1>
    <p>Exported ${new Date().toLocaleString()} · ${rows.length} results</p>
    <table><thead><tr><th>Name</th><th>Email</th><th>Date</th><th>Plan</th><th>Status</th></tr></thead>
    <tbody>${tableRows}</tbody></table>
    </body></html>`
  const w = window.open('', '_blank')
  w.document.write(html)
  w.document.close()
}

// =============================================================================
// APP TAB - SESSION DETAIL MODAL COMPONENT
// =============================================================================
const AppSessionDetailModal = ({ isOpen, onClose, session, sets, chat, loading }) => {
  if (!isOpen) return null

  const isCompleted = session?.actual_date != null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-amber-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Session Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">{formatDate(session?.actual_date || session?.planned_date)}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className={`text-xs font-medium ${isCompleted ? 'text-teal-600' : 'text-amber-600'}`}>
                {isCompleted ? 'Completed' : 'Pending'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center text-gray-500 py-10">Loading session details...</div>
          ) : (
            <div className="space-y-0">
              {/* SECTION 1: Exercise Sets */}
              <CollapsibleSection 
                id="exercise-sets" 
                title={`🏋️ Exercise Sets (${sets?.length || 0} exercises)`} 
                defaultExpanded={true}
              >
                {sets && sets.length > 0 ? (
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Exercise</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Reps (Target/Actual)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Hold (Target/Actual)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Progression</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Regression</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {sets.map((set, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-700">{set.exercise_name}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {set.reps_target} / <span className={set.reps_actual < set.reps_target ? 'text-amber-600' : 'text-teal-600'}>{set.reps_actual}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {set.hold_target}s / <span className={set.hold_actual < set.hold_target ? 'text-amber-600' : 'text-teal-600'}>{set.hold_actual}s</span>
                          </td>
                          <td className="px-3 py-2">
                            {set.progression ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">{set.progression}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {set.regression ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">{set.regression}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Session not yet completed.</p>
                  </div>
                )}
              </CollapsibleSection>

              {/* SECTION 2: Post-Session Review */}
              <CollapsibleSection 
                id="post-session-chat" 
                title={`💬 Post-Session Review (${chat?.filter(m => m.role === 'user').length || 0} questions)`} 
                defaultExpanded={true}
              >
                {chat && chat.length > 0 ? (
                  <div className="space-y-3">
                    {chat.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`rounded-lg p-3 ${
                          msg.role === 'user'
                            ? 'bg-purple-50 border border-purple-100'
                            : 'bg-slate-50 border border-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                            {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                          </span>
                          {msg.input_method && msg.role === 'user' && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">{msg.input_method}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{msg.content}</p>
                        {msg.intent && msg.role === 'assistant' && (
                          <p className="text-xs text-slate-400 mt-1">Intent: {msg.intent}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{formatDateTime(msg.created_at)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">No post-session conversation.</p>
                  </div>
                )}
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors">
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
    </div>
  )
}
// =============================================================================
// DOWNLOAD ANALYTICS TAB COMPONENT
// =============================================================================
const DownloadAnalyticsTab = ({ startDate: parentStartDate, endDate: parentEndDate, siteFilter: parentSiteFilter, variantFilter: parentVariantFilter, variantOptions = [], linkOptions = [] }) => {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)
  const [localDateRange, setLocalDateRange] = useState("today")
  const [localCustomStart, setLocalCustomStart] = useState("")
  const [localCustomEnd, setLocalCustomEnd] = useState("")
  const [localSite, setLocalSite] = useState("myptmyra")
  const [localVariant, setLocalVariant] = useState("all")
  const [localLink, setLocalLink] = useState("all")

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  let startDate, endDate
  if (localDateRange === 'custom' && localCustomStart && localCustomEnd) {
    const r1 = getTimeZoneDateRange(timezone, localCustomStart)
    const r2 = getTimeZoneDateRange(timezone, localCustomEnd)
    startDate = r1.startDate
    endDate = r2.endDate
  } else {
    const r = getTimeZoneDateRange(timezone, localDateRange)
    startDate = r.startDate
    endDate = r.endDate
  }

  const fetchAndDownload = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) axios.defaults.headers.common["Authorization"] = token

      const resp = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/export/full?start_date=${startDate}&end_date=${endDate}&site=${localSite}&variant=${localVariant}&link=${localLink}`
      )
      console.log("response is : ",resp)

      const sessions = resp.data?.responseData?.sessions || []

      const exportPayload = {
        exported_at: new Date().toISOString(),
        date_range: { start_date: startDate, end_date: endDate },
        filters: { site: localVariant, variant: localSite, link: localLink },
        total: sessions.length,
        sessions: sessions.map(s => ({
          id: s.id,
          ...s,
        }))
      }

      setPreview({
        total: sessions.length,
        sample: sessions.slice(0, 3).map(s => ({
          session_id: s.session_id,
          created_at: s.created_at,
          city: s.city,
          country: s.country,
          signed_up: s.signed_up,
          started_assessment: s.started_assessment,
          viewed_plan: s.viewed_plan,
          variant: s.variant,
        }))
      })

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `analytics-sessions-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Download analytics failed:", err)
      setError(err?.message || "Failed to download analytics.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Download Analytics</h2>
        <p className="text-sm text-slate-500 mb-6">
          Export all session data for the currently selected date range and filters as a JSON file.
          The file structure mirrors the <code className="bg-slate-100 px-1 rounded text-xs">assessment_funnel_analytics</code> table.
        </p>

        {/* Filter Controls */}
        <div className="flex flex-wrap gap-3 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600 whitespace-nowrap">Date Range</label>
            <select value={localDateRange} onChange={e => { setLocalDateRange(e.target.value); if (e.target.value !== 'custom') { setLocalCustomStart(''); setLocalCustomEnd(''); } }} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
            {localDateRange === 'custom' && (<>
              <input type="date" value={localCustomStart} onChange={e => setLocalCustomStart(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
              <span className="text-slate-400 text-sm">to</span>
              <input type="date" value={localCustomEnd} onChange={e => setLocalCustomEnd(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
            </>)}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Site</label>
            <select value={localSite} onChange={e => setLocalSite(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                <option value="myptmyra">MyPTMyra</option>
                <option value="all">All Sites</option>
                <option value="startfreept">StartFreePT</option>
                <option value="virdio">Virdio Health</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Variant</label>
            <select value={localVariant} onChange={e => setLocalVariant(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Variants</option>
              {variantOptions.map(v => (<option key={v} value={v}>{v}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Link Source</label>
            <select value={localLink} onChange={e => setLocalLink(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Link Sources</option>
              {linkOptions.map(v => (<option key={v} value={v}>{v}</option>))}
            </select>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Start Date</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{startDate}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">End Date</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{endDate}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Site Filter</p>
            <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{localSite}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Variant Filter</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{localVariant}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Link Source Filter</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{localLink}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}

        <button
          onClick={fetchAndDownload}
          disabled={loading}
          className="px-6 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Fetching & Downloading..." : "↓ Download Analytics JSON"}
        </button>

        {preview && (
          <div className="mt-6">
            <p className="text-sm font-medium text-slate-700 mb-3">
              ✅ Downloaded <span className="text-teal-600 font-bold">{preview.total.toLocaleString()}</span> sessions. Preview of first 3:
            </p>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="min-w-full text-xs divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    {['session_id','created_at','city','country','signed_up','started_assessment','viewed_plan','variant'].map(col => (
                      <th key={col} className="px-3 py-2 text-left font-medium text-slate-500 uppercase whitespace-nowrap">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {preview.sample.map((row, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-slate-500">{row.session_id?.substring(0,12)}...</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{row.city || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{row.country || '—'}</td>
                      <td className="px-3 py-2">{row.signed_up ? <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Yes</span> : <span className="text-slate-300">No</span>}</td>
                      <td className="px-3 py-2">{row.started_assessment ? <span className="px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold">Yes</span> : <span className="text-slate-300">No</span>}</td>
                      <td className="px-3 py-2">{row.viewed_plan ? <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Yes</span> : <span className="text-slate-300">No</span>}</td>
                      <td className="px-3 py-2 text-slate-600">{row.variant || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

// =============================================================================
// DOWNLOAD CHATS TAB COMPONENT
// =============================================================================
const DownloadChatsTab = ({ siteFilter: parentSiteFilter, variantFilter: parentVariantFilter, variantOptions = [], linkOptions = [] }) => {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState(null)
  const [localDateRange, setLocalDateRange] = useState("today")
  const [localCustomStart, setLocalCustomStart] = useState("")
  const [localCustomEnd, setLocalCustomEnd] = useState("")
  const [localSite, setLocalSite] = useState("myptmyra")
  const [localVariant, setLocalVariant] = useState("all")
  const [localLink, setLocalLink] = useState("all")

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  let startDate, endDate
  if (localDateRange === 'custom' && localCustomStart && localCustomEnd) {
    const r1 = getTimeZoneDateRange(timezone, localCustomStart)
    const r2 = getTimeZoneDateRange(timezone, localCustomEnd)
    startDate = r1.startDate
    endDate = r2.endDate
  } else {
    const r = getTimeZoneDateRange(timezone, localDateRange)
    startDate = r.startDate
    endDate = r.endDate
  }
  const fetchAndDownload = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) axios.defaults.headers.common["Authorization"] = token

      const resp = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/export/chats?start_date=${startDate}&end_date=${endDate}&site=${localSite}&variant=${localVariant}&link=${localLink}`
      )

      const chats = resp.data?.responseData?.chats || []

      const exportPayload = {
        exported_at: new Date().toISOString(),
        date_range: { start_date: startDate, end_date: endDate },
        total: chats.length,
        chats: chats.map(c => {
          const { city, country, ...rest } = c
          return rest
        })
      }

      setPreview({
        total: chats.length,
        sample: chats.slice(0, 3).map(c => ({
          session_id: c.session_id,
          touchpoint: c.touchpoint,
          turn_number: c.turn_number,
          role: c.role,
          content: c.content,
          intent: c.intent,
          created_at: c.created_at,
        }))
      })

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `chat-history-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Download chats failed:", err)
      setError(err?.message || "Failed to download chat history.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Download Chat History</h2>
        <p className="text-sm text-slate-500 mb-6">
          Export all Myra chat messages for the currently selected date range as a JSON file.
          The file structure mirrors the <code className="bg-slate-100 px-1 rounded text-xs">chat_history</code> table.
        </p>

        {/* Filter Controls */}
        <div className="flex flex-wrap gap-3 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600 whitespace-nowrap">Date Range</label>
            <select value={localDateRange} onChange={e => { setLocalDateRange(e.target.value); if (e.target.value !== 'custom') { setLocalCustomStart(''); setLocalCustomEnd(''); } }} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
            {localDateRange === 'custom' && (<>
              <input type="date" value={localCustomStart} onChange={e => setLocalCustomStart(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
              <span className="text-slate-400 text-sm">to</span>
              <input type="date" value={localCustomEnd} onChange={e => setLocalCustomEnd(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
            </>)}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Site</label>
            <select value={localSite} onChange={e => setLocalSite(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                <option value="myptmyra">MyPTMyra</option>
                <option value="all">All Sites</option>
                <option value="startfreept">StartFreePT</option>
                <option value="virdio">Virdio Health</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Variant</label>
            <select value={localVariant} onChange={e => setLocalVariant(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Variants</option>
              {variantOptions.map(v => (<option key={v} value={v}>{v}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Link Source</label>
            <select value={localLink} onChange={e => setLocalLink(e.target.value)} className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="all">All Link Sources</option>
              {linkOptions.map(v => (<option key={v} value={v}>{v}</option>))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Start Date</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{startDate}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">End Date</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{endDate}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Site</p>
            <p className="text-sm font-semibold text-slate-800 mt-1 capitalize">{localSite}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Variant</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{localVariant}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase font-medium">Link Source</p>
            <p className="text-sm font-semibold text-slate-800 mt-1">{localLink}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
        )}

        <button
          onClick={fetchAndDownload}
          disabled={loading}
          className="px-6 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Fetching & Downloading..." : "↓ Download Chat History JSON"}
        </button>

        {preview && (
          <div className="mt-6">
            <p className="text-sm font-medium text-slate-700 mb-3">
              ✅ Downloaded <span className="text-purple-600 font-bold">{preview.total.toLocaleString()}</span> messages. Preview of first 3:
            </p>
            <div className="space-y-3">
              {preview.sample.map((msg, i) => (
                <div
                  key={i}
                  className={`rounded-lg p-3 border ${msg.role === 'user' ? 'bg-purple-50 border-purple-100' : 'bg-slate-50 border-slate-100'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                      {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                    </span>
                    <span className="text-xs text-slate-400">{msg.touchpoint}</span>
                    <span className="text-xs text-slate-400">Turn {msg.turn_number}</span>
                    {msg.intent && <span className="text-xs px-1.5 py-0.5 bg-white rounded text-slate-500">{msg.intent}</span>}
                  </div>
                  <p className="text-sm text-slate-700">{msg.content}</p>
                  <p className="text-xs text-slate-400 mt-1">{msg.created_at ? new Date(msg.created_at).toLocaleString() : '—'}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

// =============================================================================
// PROMOS TAB COMPONENT
// =============================================================================
const PromosTab = () => {
  const [tier, setTier] = useState('')
  const [discountPercentage, setDiscountPercentage] = useState('')
  const [expires, setExpires] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedAt, setSavedAt] = useState(null)

  const tierLabels = { weekly: 'Weekly', four: '4-Week', eight: '8-Week', all: 'All Plans' }

  const fetchPromo = async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await axios.get(`${window.ajaxLink}cloud_dashboard/analytics/promo`)
      const data = resp.data?.responseData || {}
      setTier(data.tier || '')
      setDiscountPercentage(data.discount_percentage || '')
      setExpires(data.expires || '')
    } catch (err) {
      setError(err?.message || 'Failed to load promo settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchPromo() }, [])

  const withAuth = () => {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
    if (token) axios.defaults.headers.common["Authorization"] = token
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      withAuth()
      const resp = await axios.post(`${window.ajaxLink}cloud_dashboard/analytics/promo`, {
        tier,
        discount_percentage: discountPercentage,
        expires,
      })
      const data = resp.data?.responseData || {}
      setTier(data.tier || '')
      setDiscountPercentage(data.discount_percentage || '')
      setExpires(data.expires || '')
      setSavedAt(new Date().toLocaleString())
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save promo settings')
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!window.confirm('Clear the active promo? This removes tier, discount, and expiry.')) return
    setSaving(true)
    setError(null)
    try {
      withAuth()
      const resp = await axios.delete(`${window.ajaxLink}cloud_dashboard/analytics/promo`)
      const data = resp.data?.responseData || {}
      setTier(data.tier || '')
      setDiscountPercentage(data.discount_percentage || '')
      setExpires(data.expires || '')
      setSavedAt(new Date().toLocaleString())
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to clear promo settings')
    } finally {
      setSaving(false)
    }
  }

  const isActive = tier && discountPercentage && expires &&
    new Date(expires) >= new Date(new Date().toDateString())

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 max-w-2xl">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Pricing Promo</h2>
        <p className="text-sm text-slate-500 mb-6">
          Configure the active site-wide discount. Stored in{' '}
          <code className="bg-slate-100 px-1 rounded text-xs">global_config</code>.
        </p>

        {loading ? (
          <div className="text-slate-500 text-sm py-6 text-center">Loading promo settings...</div>
        ) : (
          <>
            {isActive && (
              <div className="mb-5 bg-green-50 border border-green-200 rounded-lg p-3">
                <span className="text-sm text-green-700 font-medium">
                  ✓ Active — {discountPercentage}% off {tierLabels[tier] || tier}, expires {expires}
                </span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Discount Tier</label>
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white"
                >
                  <option value="">Select a tier</option>
                  <option value="weekly">Weekly</option>
                  <option value="four">4-Week</option>
                  <option value="eight">8-Week</option>
                  <option value="all">All Plans</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Discount Percentage</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(e.target.value)}
                    placeholder="e.g. 15 or 12.5"
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 pr-8 bg-white"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Discount Expires</label>
                <input
                  type="date"
                  value={expires}
                  onChange={(e) => setExpires(e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
            )}

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Promo'}
              </button>
              <button
                onClick={handleClear}
                disabled={saving}
                className="px-5 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm font-semibold hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Promo
              </button>
              {savedAt && <span className="text-xs text-slate-400">Last updated: {savedAt}</span>}
            </div>
          </>
        )}
      </section>
    </div>
  )
}

// =============================================================================
// MAIN ANALYTICS PAGE COMPONENT
// =============================================================================
const AnalyticsPage = (props) => {
  const { refreshKey } = props
  
  // =============================================================================
  // TAB STATE
  // =============================================================================
  const [activeTab, setActiveTab] = useState("gsa") // "gsa" or "app"
  
  // =============================================================================
  // GSA TAB STATE (existing)
  // =============================================================================
  const [dateRange, setDateRange] = useState("today")
  const [customStartDate, setCustomStartDate] = useState("")  // C2: YYYY-MM-DD start
  const [customEndDate, setCustomEndDate] = useState("")      // C2: YYYY-MM-DD end
  const [siteFilter, setSiteFilter] = useState('myptmyra')
  const [variantFilter, setVariantFilter] = useState('all')    // C1: variant picker
  const [variantOptions, setVariantOptions] = useState([])      // C1: populated from API
  const [linkFilter, setLinkFilter] = useState('all')          // from_the_link picker
  const [linkOptions, setLinkOptions] = useState([])            // populated from API
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // get user timezone
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState("")
  // C6: SQL Query Runner state
  const [queryText, setQueryText] = useState('')
  const [queryResults, setQueryResults] = useState(null)
  const [queryLoading, setQueryLoading] = useState(false)
  const [queryError, setQueryError] = useState(null)

  // Drill-down modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalStepData, setModalStepData] = useState(null)
  const [modalUsers, setModalUsers] = useState([])
  const [modalLoading, setModalLoading] = useState(false)
  const [modalPagination, setModalPagination] = useState({ page: 1, limit: 50, total: 0 })

  // Session Details modal state
  const [sessionDetailModalOpen, setSessionDetailModalOpen] = useState(false);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [sessionDetailsLoading, setSessionDetailsLoading] = useState(false);

  // chat states
  const [chatModalOpen, setChatModalOpen] = useState(false)
  const [chatTouchpoint, setChatTouchpoint] = useState(null) // 'landing' or 'post_plan'
  const [chatSessions, setChatSessions] = useState([])
  const [chatLoading, setChatLoading] = useState(false)
  const [chatDetailsLoading, setChatDetailsLoading] = useState(false)
  const [chatPagination, setChatPagination] = useState({ page: 1, limit: 50, total: 0 })
  // Chat Detail Modal State (for individual conversation view)
  const [chatDetailModalOpen, setChatDetailModalOpen] = useState(false)
  const [selectedChatSession, setSelectedChatSession] = useState(null)
  const [chatMessages, setChatMessages] = useState([])

  // =============================================================================
  // APP TAB STATE (new - independent from GSA tab)
  // =============================================================================
  const [appDateRange, setAppDateRange] = useState("today")
  const [appCustomStartDate, setAppCustomStartDate] = useState("")
  const [appCustomEndDate, setAppCustomEndDate] = useState("")
  const [appSiteFilter, setAppSiteFilter] = useState("myptmyra")
  const [appVariantFilter, setAppVariantFilter] = useState("all")
  const [appLinkFilter, setAppLinkFilter] = useState("all")
  const [appKpis, setAppKpis] = useState(null)
  const [appUsers, setAppUsers] = useState([])
  const [appUsersPagination, setAppUsersPagination] = useState({ total: 0, limit: 50, offset: 0, page: 1 })
  const [appUsersSearch, setAppUsersSearch] = useState('')
  const [appUsersStatusFilter, setAppUsersStatusFilter] = useState('all')
  const [appUsersView, setAppUsersView] = useState('sessions') // 'sessions' | 'sginedup'
  const [appUsersSubview, setAppUsersSubview] = useState('completed') // 'completed' | 'pending' — only used when appUsersView === 'sessions'
  const [appLoading, setAppLoading] = useState(false)
  const [appLastUpdated, setAppLastUpdated] = useState("")
  const [appUsersSearchInput, setAppUsersSearchInput] = useState('')

  // Generic App-tab KPI drilldown modal (Signed Up / Active / Weekly / 8-Week / Expired / Completed / Skipped)
  const [appKpiDrilldownOpen, setAppKpiDrilldownOpen] = useState(false)
  const [appKpiDrilldownTitle, setAppKpiDrilldownTitle] = useState('')
  const [appKpiDrilldownRows, setAppKpiDrilldownRows] = useState([])
  const [appKpiDrilldownKind, setAppKpiDrilldownKind] = useState('user') // 'user' | 'session' — controls row rendering

  // App Tab Modal State
  const [appUserDetailModalOpen, setAppUserDetailModalOpen] = useState(false)
  const [selectedAppUser, setSelectedAppUser] = useState(null)
  const [selectedAppUserGSA, setSelectedAppUserGSA] = useState(null)
  const [selectedAppUserSessions, setSelectedAppUserSessions] = useState([])
  const [appUserDetailLoading, setAppUserDetailLoading] = useState(false)

  const [loadingDownloadAnalytics,setLoadingDownloadAnalytics] = useState(false)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (appUsersSearchInput !== appUsersSearch) {
        setAppUsersSearch(appUsersSearchInput)
        fetchAppUsers(1, appUsersStatusFilter, appUsersSearchInput)
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [appUsersSearchInput])

  // App Tab Session Detail Modal State
  const [appSessionDetailModalOpen, setAppSessionDetailModalOpen] = useState(false)
  const [selectedAppSession, setSelectedAppSession] = useState(null)
  const [selectedAppSessionSets, setSelectedAppSessionSets] = useState([])
  const [selectedAppSessionChat, setSelectedAppSessionChat] = useState([])
  const [appSessionDetailLoading, setAppSessionDetailLoading] = useState(false)

  // clicked on refresh -> recalling the api




  // get start and end time to fetch data 

  // C2: Custom range uses two separate date picks; presets use single call
  let startDate, endDate;
  if (dateRange === 'custom' && customStartDate && customEndDate) {
    const result1 = getTimeZoneDateRange(timezone, customStartDate);
    const result2 = getTimeZoneDateRange(timezone, customEndDate);
    startDate = result1.startDate;
    endDate = result2.endDate;
  } else {
    const result = getTimeZoneDateRange(timezone, dateRange);
    startDate = result.startDate;
    endDate = result.endDate;
  }
// App Tab date range calculation with custom range 
  let appStartDate, appEndDate;
  if (appDateRange === 'custom' && appCustomStartDate && appCustomEndDate) {
    const result1 = getTimeZoneDateRange(timezone, appCustomStartDate);
    const result2 = getTimeZoneDateRange(timezone, appCustomEndDate);
    appStartDate = result1.startDate;
    appEndDate = result2.endDate;
  } else {
    const result = getTimeZoneDateRange(timezone, appDateRange);
    appStartDate = result.startDate;
    appEndDate = result.endDate;
  }
  const formatAppQueryDateRange = () => {
  if (appDateRange === 'all') return 'All Time';

  const startPart = (appStartDate || '').split(' ')[0]; // strip time component
  const endPart = (appEndDate || '').split(' ')[0];

  if (appDateRange === 'custom' && appCustomStartDate && appCustomEndDate) {
    return appCustomStartDate === appCustomEndDate
      ? formatDate(appCustomStartDate)
      : `${formatDate(appCustomStartDate)} – ${formatDate(appCustomEndDate)}`;
  }

  if (appDateRange === 'today' || appDateRange === 'yesterday') {
    return formatDate(startPart);
  }

  // wtd, mtd, 7d, 30d, 90d — whatever getTimeZoneDateRange actually resolves to
  return startPart === endPart
    ? formatDate(startPart)
    : `${formatDate(startPart)} – ${formatDate(endPart)}`;
};
  // =============================================================================
  // GSA TAB API CALLS (existing)
  // =============================================================================

  // Fetch session Details for landed on startFreePt 
  const fetchSessionDetails = async (sessionId) => {
  
    setSessionDetailsLoading(true);

  try {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token;

    if (token) {
          axios.defaults.headers.common["Authorization"] = token
        }

        const response = await axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/drilldown/session_details?sessionId=${sessionId}&range=${dateRange}&start_date=${startDate}&end_date=${endDate}`
        )

        const result = response.data?.responseData
        console.log("sessionDetails", result)

    setSelectedSessionDetails(result|| null);
   
  } catch (err) {
    console.error("Failed to fetch session details:", err);
    setSelectedSessionDetails(null);
  } finally {
    setSessionDetailsLoading(false);
  }
};

  // helper funtion for session details 
  const handleSessionClick = (sessionId) => {
  setSessionDetailModalOpen(true);
  fetchSessionDetails(sessionId);
};


  // fetching chats
  // Fetch chat sessions list for a specific touchpoint
  // API now returns pre-aggregated sessions with question_count and message_count
  const fetchChatSessions = useCallback(async (touchpoint, page = 1) => {
      setChatLoading(true)

      try {
        const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
        if (token) {
          axios.defaults.headers.common["Authorization"] = token
        }

        const chatRange = activeTab === 'app' ? appDateRange : dateRange
        const chatStartDate = activeTab === 'app' ? appStartDate : startDate
        const chatEndDate = activeTab === 'app' ? appEndDate : endDate
        const chatSiteFilter = activeTab === 'app' ? appSiteFilter : siteFilter
        const chatVariantFilter = activeTab === 'app' ? appVariantFilter : variantFilter
        const chatLinkFilter = activeTab === 'app' ? appLinkFilter : linkFilter

        const response = await axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/prechats?range=${chatRange}&page=${page}&limit=50&touchpoint=${touchpoint}&start_date=${chatStartDate}&end_date=${chatEndDate}&site=${chatSiteFilter}&variant=${chatVariantFilter}&link=${chatLinkFilter}`
        )

        const result = response.data?.responseData
        console.log("Chat results are : ",result)
        
        // API now returns pre-aggregated sessions (not individual messages)
        // Each item already has session_id, question_count, message_count, etc.
        const sessions = result?.chats?.map(session => ({
          session_id: session.session_id,
          user_id: session.user_id,
          conversation_id: session.conversation_id,
          touchpoint: session.touchpoint,
          created_at: session.created_at,
          message_count: session.message_count || 0,
          question_count: session.question_count || 0,
          location: [session.city, session.country].filter(Boolean).join(', ') || 'Unknown',
          device: session.device_type || 'Unknown',
          signed_up: session.signed_up,
          started_gsa: session.started_assessment,
          completed_gsa: session.completed_assessment
        })) || []

        setChatSessions(sessions)
        const rawTotal = result?.total
        const numericTotal = (rawTotal && typeof rawTotal === 'object')
          ? (rawTotal.total ?? 0)
          : (rawTotal || 0)
        setChatPagination({
          page: result?.page || 1,
          limit: result?.limit || 50,
          total: numericTotal,
        })
      } catch (err) {
        console.error("Failed to fetch chat sessions:", err)
        setChatSessions([])
      } finally {
        setChatLoading(false)
      }
  }, [activeTab, dateRange, startDate, endDate, siteFilter, variantFilter, linkFilter, appDateRange, appStartDate, appEndDate, appSiteFilter, appVariantFilter])

  // handlers for chat:
  // Open chat modal for specific touchpoint
const openChatModal = (touchpoint) => {
  setChatTouchpoint(touchpoint)
  setChatModalOpen(true)
  fetchChatSessions(touchpoint, 1)
}

// Handle chat modal pagination
const handleChatPageChange = (newPage) => {
  if (newPage < 1) return
  fetchChatSessions(chatTouchpoint, newPage)
}

// Open detail view for a specific chat session
// For post_session, we use conversation_id; for others, we use session_id
const openChatDetail = (session) => {
  setSelectedChatSession(session)
  setChatDetailModalOpen(true)
  
  // Fetch full conversation details
  // For post_session, pass conversation_id; for others, pass session_id
  if (chatTouchpoint === 'post_session') {
    fetchChatDetail(null, session.conversation_id)
  } else {
    fetchChatDetail(session.session_id, null)
  }
}

// Fetch full conversation for a session or conversation
const fetchChatDetail = async (sessionId, conversationId = null) => {
  setChatDetailsLoading(true)
  try {
    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
    if (token) {
      axios.defaults.headers.common["Authorization"] = token
    }

    // Build query params - use conversation_id for post_session, session_id for others
    const chatRange = activeTab === 'app' ? appDateRange : dateRange
    const chatStartDate = activeTab === 'app' ? appStartDate : startDate
    const chatEndDate = activeTab === 'app' ? appEndDate : endDate
    const chatSiteFilter = activeTab === 'app' ? appSiteFilter : siteFilter
    const chatVariantFilter = activeTab === 'app' ? appVariantFilter : variantFilter
    const chatLinkFilter = activeTab === 'app' ? appLinkFilter : linkFilter
    let url = `${window.ajaxLink}cloud_dashboard/analytics/prechats?range=${chatRange}&touchpoint=${chatTouchpoint}&start_date=${chatStartDate}&end_date=${chatEndDate}&site=${chatSiteFilter}&variant=${chatVariantFilter}&link=${chatLinkFilter}`
    if (conversationId) {
      url += `&conversation_id=${conversationId}`
    } else if (sessionId) {
      url += `&session_id=${sessionId}`
    }

    const response = await axios.get(url)

    const result = response.data?.responseData
    setChatMessages(result?.chats || [])
  } catch (err) {
    console.error("Failed to fetch chat details:", err)
    setChatMessages([])
  } finally {
    setChatDetailsLoading(false)
  }
}

// chat modal
// =============================================================================
// MYRA CHAT SESSIONS MODAL COMPONENT
// =============================================================================
const ChatSessionsModal = ({ isOpen, onClose, touchpoint, sessions, loading, pagination, onPageChange, onSessionClick }) => {
  if (!isOpen) return null

  const touchpointLabels = {
    landing: 'Landing Page',
    post_plan: 'Post-Assessment',
    post_session: 'In-App (Post-Session)'
  }
  const touchpointLabel = touchpointLabels[touchpoint] || touchpoint
  const showUserId = touchpoint === 'post_session' // Show user_id for in-app chats

  // Status badge helper
  const getSessionStatus = (session) => {
    if (session.signed_up) return { text: 'Signed Up', badge: 'bg-blue-100 text-blue-700' }
    if (session.completed_gsa) return { text: 'Completed GSA', badge: 'bg-green-100 text-green-700' }
    if (session.started_gsa) return { text: 'Started GSA', badge: 'bg-yellow-100 text-yellow-700' }
    return { text: '', badge: '' }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-purple-800">
              Myra Conversations: <span className="text-purple-600">{touchpointLabel}</span>
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {pagination.total?.toLocaleString()} users asked questions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openChatExportTab(touchpoint, sessions)}
              className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium"
              title="Open full chat list in new tab"
            >
              ↗ New Tab
            </button>
            <button
              onClick={() => downloadChatSessionsJSON(sessions, touchpoint)}
              className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium"
              title="Download as CSV"
            >
              ↓ CSV
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 text-3xl leading-none"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Sessions List - 2 column grid with compact cards */}
        <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading chat sessions...</div>
          ) : sessions.length === 0 ? (
            <div className="col-span-2 text-center py-8 text-slate-500">No conversations found.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sessions.map((session, index) => {
                const status = getSessionStatus(session)
                const questionCount = session.question_count || session.message_count || 0
                return (
                  <div
                    key={session.conversation_id || session.session_id || index}
                    onClick={() => onSessionClick(session)}
                    className="border border-purple-100 rounded-lg p-3 hover:bg-purple-50 cursor-pointer transition-all hover:shadow-md flex justify-between items-center"
                  >
                    <div className="space-y-1">
                      {/* Line 1: Date/Time + Q count badge */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-700">
                          {formatDateTime(session.created_at)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                          {questionCount} Q
                        </span>
                      </div>
                      {/* Line 2: User ID (for post_session) OR Location • Device */}
                      {showUserId && session.user_id ? (
                        <div className="text-sm text-blue-600 font-medium">
                          User: {session.user_id}
                        </div>
                      ) : (
                        <div className="text-sm text-slate-600">
                          {session.location || 'Unknown'} • {session.device || 'Unknown'}
                        </div>
                      )}
                      {/* Line 3: Session ID or Conversation ID (truncated) + copy button */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">
                          {(session.conversation_id || session.session_id)?.substring(0, 8)}...
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(session.conversation_id || session.session_id || '')
                            e.target.textContent = '✓'
                            setTimeout(() => { e.target.textContent = '📋' }, 1500)
                          }}
                          className="text-xs text-slate-400 hover:text-purple-600"
                          title="Copy ID"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                    {/* Status badge on right */}
                    <div className="flex items-center">
                      {status.text && (
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${status.badge}`}>
                          {status.text}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
          <button
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <span className="text-sm text-slate-500">
            Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <button
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page * pagination.limit >= pagination.total}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
// =============================================================================
// CHAT DETAIL MODAL COMPONENT (shows full conversation)
// =============================================================================
const ChatDetailModal = ({ isOpen, onClose, session, loading, messages,onNewTab }) => {
  if (!isOpen) return null

  // Sort messages chronologically (oldest first) for natural reading flow
  const sortedMessages = [...messages].sort((a, b) => {
    const dateCompare = new Date(a.created_at) - new Date(b.created_at);
    if (dateCompare !== 0) return dateCompare;
    return (a.turn_number || 0) - (b.turn_number || 0);
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-lg font-semibold text-purple-800">Conversation Details</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">{session?.location || 'Unknown Location'}</span>
              <span className="text-xs text-slate-400">•</span>
              <span className="text-xs text-slate-500">{session?.device || 'Unknown Device'}</span>
              {session?.user_id && (
                <>
                  <span className="text-xs text-slate-400">•</span>
                  <span className="text-xs text-blue-600 font-medium">User: {session.user_id}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadChatDetailJSON(sortedMessages, session)}
              className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium"
            >
              ↓ Download Conversation
            </button>
            <button
              onClick={onNewTab}
              disabled={!sortedMessages?.length}
              className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ↗ New Tab
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-3xl leading-none">&times;</button>
          </div>
        </div>

        {/* Chat Messages - Chronological order, oldest at top */}
        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-4">
          {loading ? (
            <div className="text-center py-10 text-slate-500">Loading messages...</div>
          ) : sortedMessages.length === 0 ? (
            <div className="text-center py-10 text-slate-500">No messages in this conversation.</div>
          ) : (
            sortedMessages.map((msg, index) => (
              <div
                key={msg.id || index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${msg.role === 'user' ? 'bg-purple-50 border border-purple-100' : 'bg-slate-50 border border-slate-100'} rounded-lg p-4`}>
                  {/* Message Header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold ${msg.role === 'user' ? 'text-purple-700' : 'text-slate-600'}`}>
                      {msg.role === 'user' ? '👤 User' : '🤖 Myra'}
                    </span>
                    {msg.input_method && msg.role === 'user' && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-white text-slate-500">
                        {msg.input_method}
                      </span>
                    )}
                  </div>

                  {/* Message Content */}
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{msg.content}</p>

                  {/* Intent Info - only for assistant */}
                  {msg.intent && msg.role === 'assistant' && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <span className="text-xs text-slate-500">
                        Intent: <span className="font-medium text-slate-700">{msg.intent}</span>
                        {msg.intent_confidence && (
                          <span className="ml-2 text-slate-400">
                            ({(msg.intent_confidence * 100).toFixed(0)}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* Timestamp */}
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
  const handleDownloadAnalytics = async () => {
    try {
      setLoadingDownloadAnalytics(true)
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token;
      if (token) axios.defaults.headers.common["Authorization"] = token;

      const analyticsResp = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/export/full?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&variant=${variantFilter}&link=${linkFilter}`
      );

      const sessions = analyticsResp.data?.responseData?.sessions || [];

      // Shape matches assessment_funnel_analytics table structure
      const exportPayload = {
        exported_at: new Date().toISOString(),
        date_range: { start_date: startDate, end_date: endDate },
        filters: { site: siteFilter, variant: variantFilter, link: linkFilter },
        total: sessions.length,
        sessions: sessions.map(s => ({
          id: s.id,
          session_id: s.session_id,
          session_start_time: s.session_start_time || s.created_at,
          session_last_update: s.session_last_update,
          device_type: s.device_type,
          device_info: s.device_info,
          device_ip: s.device_ip,
          country: s.country,
          city: s.city,
          started_assessment: s.started_assessment,
          completed_safety_screen: s.completed_safety_screen,
          red_flag_exit: s.red_flag_exit,
          selected_pain_zones: s.selected_pain_zones,
          cpg_classification: s.cpg_classification,
          pre_test_nprs: s.pre_test_nprs,
          symptom_duration: s.symptom_duration,
          probable_subtype: s.probable_subtype,
          rom_test1_name: s.rom_test1_name,
          rom_test1_response: s.rom_test1_response,
          rom_test1_completed: s.rom_test1_completed,
          rom_test1_rpe: s.rom_test1_rpe,
          rom_test2_name: s.rom_test2_name,
          rom_test2_response: s.rom_test2_response,
          rom_test2_completed: s.rom_test2_completed,
          completed_assessment: s.completed_assessment,
          final_recovery_score: s.final_recovery_score,
          viewed_plan: s.viewed_plan,
          final_conversion_type: s.final_conversion_type,
          signed_up: s.signed_up,
          myra_engaged_landing: s.myra_engaged_landing,
          myra_engaged_gsa: s.myra_engaged_gsa,
          myra_engaged_post_plan: s.myra_engaged_post_plan,
          myra_conversation_count: s.myra_conversation_count,
          gsa_duration_sec: s.gsa_duration_sec,
          time_on_landing_sec: s.time_on_landing_sec,
          isComeFromGoogle: s.isComeFromGoogle,
          isUserCameBackAgain: s.isUserCameBackAgain,
          variant: s.variant,
          exit_reason: s.exit_reason,
          gsa_step: s.gsa_step,
          created_at: s.created_at,
        }))
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-sessions-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download analytics failed:", err);
      alert("Failed to download analytics. Check console for details.");
    } finally {
      setLoadingDownloadAnalytics(false)
    }
  };

  const handleDownloadChatHistory = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token;
      if (token) axios.defaults.headers.common["Authorization"] = token;

      const chatsResp = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/export/chats?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&variant=${variantFilter}&link=${linkFilter}`
      );

      const chats = chatsResp.data?.responseData?.chats || [];

      // Shape matches chat_history table structure
      const exportPayload = {
        exported_at: new Date().toISOString(),
        date_range: { start_date: startDate, end_date: endDate },
        total: chats.length,
        chats: chats.map(c => ({
          id: c.id,
          session_id: c.session_id,
          user_id: c.user_id,
          conversation_id: c.conversation_id,
          touchpoint: c.touchpoint,
          turn_number: c.turn_number,
          role: c.role,
          content: c.content,
          intent: c.intent,
          intent_confidence: c.intent_confidence,
          input_method: c.input_method,
          city: c.city,
          country: c.country,
          device_type: c.device_type,
          signed_up: c.signed_up,
          started_assessment: c.started_assessment,
          completed_assessment: c.completed_assessment,
          created_at: c.created_at,
        }))
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-history-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download chat history failed:", err);
      alert("Failed to download chat history. Check console for details.");
    }
  };

  const handleViewAnalytics = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token;
      if (token) axios.defaults.headers.common["Authorization"] = token;

      const [analyticsResp, chatsResp] = await Promise.all([
        axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/export/full?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&variant=${variantFilter}&link=${linkFilter}`
        ),
        axios.get(
          `${window.ajaxLink}cloud_dashboard/analytics/export/chats?start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&variant=${variantFilter}&link=${linkFilter}`
        )
      ]);

      const sessions = analyticsResp.data?.responseData?.sessions || [];
      const allChats = chatsResp.data?.responseData?.chats || [];
      const dateLabel = `${startDate} → ${endDate}`;

      // Build summary stats from session data
      const totalSessions = sessions.length;
      const startedGsa = sessions.filter(s => s.started_assessment).length;
      const viewedPlan = sessions.filter(s => s.viewed_plan).length;
      const signedUp = sessions.filter(s => s.signed_up || s.final_conversion_type === 'signup').length;

      const sessionRows = sessions.map(s => {
        const chatCount = (s.chats || []).filter(c => c.role === 'user').length;
        const site = s.device_type ? s.device_type.split('|')[1]?.trim() : null;
        const siteBadge = site
          ? `<span class="badge ${site.toLowerCase().includes('startfreept') ? 'teal' : 'purple'}">${site.toLowerCase().includes('startfreept') ? 'StartFreePT' : 'Virdio'}</span>`
          : '';
        const status = s.signed_up || s.final_conversion_type === 'signup'
          ? '<span class="badge green">Signed Up</span>'
          : s.viewed_plan
            ? '<span class="badge blue">Completed GSA</span>'
            : s.started_assessment
              ? '<span class="badge yellow">Started GSA</span>'
              : '<span class="badge gray">Landed</span>';
        return `<tr onclick="toggleChats('${s.session_id}')" style="cursor:pointer">
          <td>${s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
          <td>${siteBadge}</td>
          <td>${[s.city, s.country].filter(Boolean).join(', ') || '—'}</td>
          <td>${(s.device_type || '').split('|')[0]?.trim() || '—'}</td>
          <td>${status}</td>
          <td>${s.cpg_classification || '—'}</td>
          <td>${s.rom_test1_name || '—'}</td>
          <td>${s.final_recovery_score || '—'}</td>
          <td>${chatCount > 0 ? `<span class="badge purple">${chatCount} msgs</span>` : '—'}</td>
          <td><code style="font-size:10px">${(s.session_id || '').substring(0, 10)}...</code></td>
        </tr>
        <tr id="chats-${s.session_id}" style="display:none;background:#faf5ff">
          <td colspan="10" style="padding:16px">
            ${(s.chats || []).length === 0
              ? '<em style="color:#94a3b8">No chat messages</em>'
              : (s.chats || []).map(m => `
                <div style="display:flex;justify-content:${m.role === 'user' ? 'flex-end' : 'flex-start'};margin-bottom:8px">
                  <div style="max-width:70%;background:${m.role === 'user' ? '#f3e8ff' : '#f8fafc'};border:1px solid ${m.role === 'user' ? '#e9d5ff' : '#e2e8f0'};border-radius:8px;padding:8px 12px">
                    <div style="font-size:11px;font-weight:600;color:${m.role === 'user' ? '#7e22ce' : '#64748b'};margin-bottom:4px">
                      ${m.role === 'user' ? '👤 User' : '🤖 Myra'} · ${m.touchpoint || ''} ${m.input_method ? `· ${m.input_method}` : ''}
                    </div>
                    <div style="font-size:13px;color:#334155">${(m.content || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
                    ${m.intent ? `<div style="font-size:10px;color:#94a3b8;margin-top:4px">Intent: ${m.intent}</div>` : ''}
                  </div>
                </div>`).join('')}
          </td>
        </tr>`;
      }).join('');

      // Build standalone chats section (users without funnel sessions)
      const chatRows = allChats.slice(0, 200).map((c, i) => `
        <tr>
          <td>${c.created_at ? new Date(c.created_at).toLocaleString() : '—'}</td>
          <td>${c.touchpoint || '—'}</td>
          <td><span class="badge ${c.role === 'user' ? 'purple' : 'gray'}">${c.role}</span></td>
          <td style="max-width:400px;word-break:break-word">${(c.content || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</td>
          <td>${c.intent || '—'}</td>
          <td>${c.input_method || '—'}</td>
          <td>${[c.city, c.country].filter(Boolean).join(', ') || '—'}</td>
          <td><code style="font-size:10px">${(c.session_id || '').substring(0, 10)}...</code></td>
        </tr>`).join('');

      const html = `<!DOCTYPE html><html><head><title>Analytics Export — ${dateLabel}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: system-ui, sans-serif; padding: 24px; color: #1e293b; max-width: 1400px; margin: 0 auto; }
        h1 { font-size: 22px; margin-bottom: 4px; }
        h2 { font-size: 17px; margin: 28px 0 12px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; }
        .meta { color: #64748b; font-size: 13px; margin-bottom: 20px; }
        .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
        .kpi { background: #f8fafc; border-radius: 8px; padding: 14px; }
        .kpi .label { font-size: 11px; text-transform: uppercase; color: #94a3b8; font-weight: 600; }
        .kpi .value { font-size: 24px; font-weight: 700; color: #0f172a; margin-top: 4px; }
        .kpi .sub { font-size: 12px; color: #64748b; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #f8fafc; text-align: left; padding: 8px 10px; border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #94a3b8; white-space: nowrap; }
        td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
        tr:hover td { background: #fafafa; }
        .badge { padding: 2px 7px; border-radius: 99px; font-size: 11px; font-weight: 600; display: inline-block; }
        .badge.green { background: #dcfce7; color: #15803d; }
        .badge.blue { background: #dbeafe; color: #1d4ed8; }
        .badge.yellow { background: #fef9c3; color: #854d0e; }
        .badge.gray { background: #f1f5f9; color: #475569; }
        .badge.purple { background: #f3e8ff; color: #7e22ce; }
        .badge.teal { background: #ccfbf1; color: #0f766e; }
        code { font-size: 10px; color: #64748b; }
        .section-note { font-size: 12px; color: #94a3b8; margin-bottom: 10px; }
      </style>
      <script>
        function toggleChats(sessionId) {
          const row = document.getElementById('chats-' + sessionId);
          if (row) row.style.display = row.style.display === 'none' ? 'table-row' : 'none';
        }
      </script>
      </head><body>
      <h1>📊 Analytics Export</h1>
      <div class="meta">
        Date range: <strong>${dateLabel}</strong> &nbsp;·&nbsp; 
        Site: <strong>${siteFilter}</strong> &nbsp;·&nbsp; 
        Variant: <strong>${variantFilter}</strong> &nbsp;·&nbsp; 
        Link Source: <strong>${linkFilter}</strong> &nbsp;·&nbsp; 
        Exported: <strong>${new Date().toLocaleString()}</strong>
      </div>

      <div class="kpi-grid">
        <div class="kpi">
          <div class="label">Total Sessions</div>
          <div class="value">${totalSessions.toLocaleString()}</div>
          <div class="sub">All visitors</div>
        </div>
        <div class="kpi">
          <div class="label">Started GSA</div>
          <div class="value">${startedGsa.toLocaleString()}</div>
          <div class="sub">${totalSessions > 0 ? ((startedGsa/totalSessions)*100).toFixed(1) : 0}% of visitors</div>
        </div>
        <div class="kpi">
          <div class="label">Completed GSA</div>
          <div class="value">${viewedPlan.toLocaleString()}</div>
          <div class="sub">${startedGsa > 0 ? ((viewedPlan/startedGsa)*100).toFixed(1) : 0}% of started</div>
        </div>
        <div class="kpi">
          <div class="label">Signed Up</div>
          <div class="value" style="color:#0f766e">${signedUp.toLocaleString()}</div>
          <div class="sub">${startedGsa > 0 ? ((signedUp/startedGsa)*100).toFixed(1) : 0}% of started</div>
        </div>
      </div>

      <h2>🗂️ Sessions (${totalSessions.toLocaleString()} total) — click row to expand chats</h2>
      <p class="section-note">Click any row to expand/collapse that session's chat history inline.</p>
      <table>
        <thead><tr>
          <th>Date/Time</th><th>Site</th><th>Location</th><th>Device</th>
          <th>Status</th><th>CPG</th><th>Test 1</th><th>Score</th><th>Chats</th><th>Session ID</th>
        </tr></thead>
        <tbody>${sessionRows}</tbody>
      </table>

      <h2>💬 All Chat Messages (${allChats.length.toLocaleString()} total${allChats.length > 200 ? ' — showing first 200' : ''})</h2>
      <table>
        <thead><tr>
          <th>Date/Time</th><th>Touchpoint</th><th>Role</th><th>Content</th>
          <th>Intent</th><th>Input</th><th>Location</th><th>Session ID</th>
        </tr></thead>
        <tbody>${chatRows}</tbody>
      </table>
      </body></html>`;

      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
    } catch (err) {
      console.error("View analytics failed:", err);
      alert("Failed to open analytics view. Check console for details.");
    }
  };

  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  const fetchDashboardData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

    const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics?range=${dateRange}&start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&variant=${variantFilter}&link=${linkFilter}`
      )
      setData(response.data?.responseData)
      setVariantOptions(response.data?.responseData?.variants || [])
      setLinkOptions(response.data?.responseData?.fromTheLinkOptions || [])
      setLastUpdated(new Date().toLocaleString())
    } catch (err) {
      setError(err?.message || "An unknown error occurred")
    } finally {
      setLoading(false)
    }
  }, [dateRange, startDate, endDate, siteFilter, variantFilter, linkFilter])

  const fetchDrillDownUsers = useCallback(async (stepId, page = 1) => {
    setModalLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const response = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/drilldown?step=${stepId}&range=${dateRange}&page=${page}&limit=50&start_date=${startDate}&end_date=${endDate}&site=${siteFilter}&variant=${variantFilter}&link=${linkFilter}`
      )

      const result = response.data?.responseData
      console.log("Drill-down users result:", result)
      setModalUsers(result?.users || [])
      setModalPagination({
        page: result?.page || 1,
        limit: result?.limit || 50,
        total: result?.total || 0,
      })
    } catch (err) {
      console.error("Failed to fetch drill-down users:", err)
      setModalUsers([])
    } finally {
      setModalLoading(false)
    }
  }, [dateRange, startDate, endDate, siteFilter, variantFilter, linkFilter])

  useEffect(() => {
    fetchDashboardData()
  }, [fetchDashboardData])

  // =============================================================================
  // APP TAB DATA FETCHING (new)
  // =============================================================================
const fetchAppUsers = useCallback(async (page = 1, status = 'all', search = '', subview = appUsersSubview) => {
    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      const offset = (page - 1) * 50
      let url = `${window.ajaxLink}cloud_dashboard/analytics/app/users?startDate=${appStartDate}&endDate=${appEndDate}&site=${appSiteFilter}&variant=${appVariantFilter}&link=${appLinkFilter}&view=${appUsersView}&subview=${subview}&limit=50&offset=${offset}`
      
      if (status && status !== 'all') {
        url += `&status=${status}`
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`
      }

      const response = await axios.get(url)
      const result = response.data?.responseData

      const total = result?.pagination?.total ?? 0
      const limit = result?.pagination?.limit ?? 50

      setAppUsers(result?.users || [])
      setAppUsersPagination({
        total,
        limit,
        offset: result?.pagination?.offset || 0,
        page
      })
    } catch (err) {
      console.error("Failed to fetch app users:", err)
      setAppUsers([])
    }
  }, [appStartDate, appEndDate, appSiteFilter, appVariantFilter, appLinkFilter, appUsersView, appUsersSubview])

  const fetchAppData = useCallback(async () => {
    setAppLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      // Fetch App KPIs
      const kpisResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/kpis?startDate=${appStartDate}&endDate=${appEndDate}&site=${appSiteFilter}&variant=${appVariantFilter}&link=${appLinkFilter}`
      )
      setAppKpis(kpisResponse.data?.responseData)

      // Fetch App Users
      await fetchAppUsers(1, appUsersStatusFilter, appUsersSearch, appUsersSubview)

      setAppLastUpdated(new Date().toLocaleString())
    } catch (err) {
      console.error("Failed to fetch app data:", err)
    } finally {
      setAppLoading(false)
    }
  }, [appStartDate, appEndDate, appSiteFilter, appVariantFilter, appLinkFilter, appUsersStatusFilter, appUsersSearch, appUsersSubview, fetchAppUsers])


  const fetchAppUserDetails = useCallback(async (userId) => {
    setAppUserDetailLoading(true)

    const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
    if (token) {
      axios.defaults.headers.common["Authorization"] = token
    }

    // Fetch GSA data — independent of the sessions call below
    try {
      const gsaResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/users/${userId}/gsa`
      )
      setSelectedAppUserGSA(gsaResponse.data?.responseData || null)
    } catch (err) {
      console.error("Failed to fetch app user GSA:", err)
      setSelectedAppUserGSA(null)
    }

    // Fetch sessions — independent of the GSA call above
    // Intentionally not scoped by appStartDate/appEndDate: this modal shows the user's
    // full exercise session history (past, pending, and future), not just sessions that
    // fall inside the KPI table's currently selected date range.
    try {
      const sessionsResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/users/${userId}/sessions`
      )
      setSelectedAppUserSessions(sessionsResponse.data?.responseData?.sessions || [])
    } catch (err) {
      console.error("Failed to fetch app user sessions:", err)
      setSelectedAppUserSessions([])
    }

    setAppUserDetailLoading(false)
  }, [])

      useEffect(() => {
    if (refreshKey > 0) {
      if (activeTab === 'gsa') {
        fetchDashboardData()
      } else {
        fetchAppData()
      }
    }
  }, [refreshKey, activeTab, fetchDashboardData, fetchAppData])

    // Fetch App data when tab changes or date range changes
  useEffect(() => {
    if (activeTab === 'app') {
      fetchAppData()
    }
    }, [activeTab, appDateRange, appSiteFilter, appVariantFilter, appLinkFilter, fetchAppData])

  // Re-fetch just the users table when the Sessions/Chats toggle changes
  useEffect(() => {
    if (activeTab === 'app') {
      fetchAppUsers(1, appUsersStatusFilter, appUsersSearch, appUsersSubview)
    }
  }, [activeTab, appUsersSearch, appUsersStatusFilter, appUsersView, appUsersSubview, fetchAppUsers])

  const fetchAppSessionDetails = useCallback(async (sessionId) => {
    setAppSessionDetailLoading(true)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) {
        axios.defaults.headers.common["Authorization"] = token
      }

      // Fetch sets
      const setsResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/sessions/${sessionId}/sets`
      )
      setSelectedAppSessionSets(setsResponse.data?.responseData?.sets || [])

      // Fetch chat
      const chatResponse = await axios.get(
        `${window.ajaxLink}cloud_dashboard/analytics/app/sessions/${sessionId}/chat`
      )
      setSelectedAppSessionChat(chatResponse.data?.responseData?.messages || [])

    } catch (err) {
      console.error("Failed to fetch app session details:", err)
      setSelectedAppSessionSets([])
      setSelectedAppSessionChat([])
    } finally {
      setAppSessionDetailLoading(false)
    }
  }, [])


  // =============================================================================
  // C6: SQL QUERY RUNNER HANDLERS
  // =============================================================================
  const runCustomQuery = async () => {
    setQueryLoading(true)
    setQueryError(null)
    setQueryResults(null)

    try {
      const token = JSON.parse(localStorage.getItem("userData") || "{}")?.token
      if (token) axios.defaults.headers.common["Authorization"] = token

      const response = await axios.post(
        `${window.ajaxLink}cloud_dashboard/analytics/query`,
        { sql: queryText }
      )
      setQueryResults(response.data?.responseData)
    } catch (err) {
      setQueryError(err.response?.data?.error || err.message || "Query failed")
    } finally {
      setQueryLoading(false)
    }
  }

  const exportQueryCSV = () => {
    if (!queryResults?.rows?.length) return

    const columns = queryResults.columns
    const csvRows = [columns.join(',')]

    queryResults.rows.forEach(row => {
      csvRows.push(columns.map(col => {
        const val = row[col]
        if (val === null || val === undefined) return ''
        const str = String(val)
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`
        }
        return str
      }).join(','))
    })

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics-query-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // =============================================================================
  // APP TAB HANDLERS
  // =============================================================================
  const handleAppUserRowClick = (user) => {
    setSelectedAppUser(user)
    setAppUserDetailModalOpen(true)
    fetchAppUserDetails(user.user_id)
  }

  const handleAppSessionRowClick = (session) => {
    setSelectedAppSession(session)
    setAppSessionDetailModalOpen(true)
    fetchAppSessionDetails(session.session_id)
  }

  const handleAppUsersPageChange = (newPage) => {
    if (newPage < 1) return
    fetchAppUsers(newPage, appUsersStatusFilter, appUsersSearch)
  }

  const handleAppUsersSearch = (searchTerm) => {
    setAppUsersSearch(searchTerm)
    fetchAppUsers(1, appUsersStatusFilter, searchTerm)
  }

  const handleAppUsersStatusFilter = (status) => {
    setAppUsersStatusFilter(status)
    fetchAppUsers(1, status, appUsersSearch)
  }

  const handleAppKpiClick = (filterType) => {
    // Set filter based on KPI clicked
    if (filterType === 'active' || filterType === 'engaged') {
      // For now, these would need backend support
      // We'll just refresh the users table
      fetchAppUsers(1, 'all', appUsersSearch)
    } else if (['F', 'P', 'E'].includes(filterType)) {
      handleAppUsersStatusFilter(filterType)
    }
  }
  const openAppKpiDrilldown = (listKey, title, kind = 'user') => {
    console.log('Opening drilldown:', listKey, 'Data:', appKpis?.[listKey]);
    setAppKpiDrilldownTitle(title)
    setAppKpiDrilldownRows(appKpis?.[listKey] || [])
    setAppKpiDrilldownKind(kind)
    setAppKpiDrilldownOpen(true)
  }
  const handleAppDrilldownRowClick = (row) => {
    if (!row.analytics_session_id) return // this user/session has no linked GSA session
    setAppKpiDrilldownOpen(false)          // close the list, then open session detail on top
    handleSessionClick(row.analytics_session_id)
  }
  const handleFreeSessionRowClick = (row) => {
    console.log('freesession clicked:', row);
  if (!row.session_id) return
  setAppKpiDrilldownOpen(false)
  setSelectedAppSession({ session_id: row.session_id, planned_date: row.start_date, actual_date: row.completed_date })
  setAppSessionDetailModalOpen(true)
  fetchAppSessionDetails(row.session_id)
}

  // =============================================================================
  // DRILL-DOWN HANDLERS
  // =============================================================================
  const openDrillDown = (step) => {
    setModalStepData(step)
    setModalOpen(true)
    fetchDrillDownUsers(step.id, 1)
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1) return
    fetchDrillDownUsers(modalStepData.id, newPage)
  }

  // =============================================================================
  // FUNNEL DATA
  // =============================================================================
  const getFunnelSteps = () => {
    if (!data) return []

    return data.funnelStepsDetailed || []
  }

  // =============================================================================
  // CHART DATA GENERATORS
  // =============================================================================
  const getCpgChartData = () => {
    if (!data?.charts?.cpg) return null
    const cpgTotal = data.charts.cpg.reduce((sum, item) => sum + item.count, 0)
    if (cpgTotal === 0) return null

    return {
      labels: data.charts.cpg.map((item) => item.cpg_classification.replace("LBP with ", "")),
      datasets: [{
        data: data.charts.cpg.map((item) => item.count),
        backgroundColor: ["#14b8a6", "#0891b2", "#f59e0b", "#6366f1"],
      }],
    }
  }

  const getCpgChartOptions = () => {
    const cpgTotal = data?.charts?.cpg?.reduce((sum, item) => sum + item.count, 0) || 0
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
        datalabels: {
          formatter: (value) => ((value * 100) / cpgTotal).toFixed(0) + "%",
          color: "#fff",
          font: { weight: "bold" },
        },
      },
    }
  }

  const getSymptomChartData = () => {
    if (!data?.charts?.symptom) return null
    const { improved, tolerable, worsened } = data.charts.symptom
    if (!improved && !tolerable && !worsened) return null

    return {
      labels: ["Improved", "Same", "Worsened"],
      datasets: [{
        label: "Users",
        data: [improved || 0, tolerable || 0, worsened || 0],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
      }],
    }
  }

  const getSymptomChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false }, datalabels: { display: false } },
  })

  const getDeviceChartData = () => {
    if (!data?.charts?.devices) return null
    const { mobile, desktop, tablet } = data.charts.devices
    if (!mobile && !desktop && !tablet) return null

    return {
      labels: ["Mobile", "Desktop", "Tablet"],
      datasets: [{
        data: [mobile || 0, desktop || 0, tablet || 0],
        backgroundColor: ["#10b981", "#3b82f6", "#f97316"],
      }],
    }
  }

  const getDeviceChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      datalabels: { display: false },
    },
  })

  const getPainZonesChartData = () => {
    if (!data?.charts?.painZones) return null
    const zones = data.charts.painZones
    if (!zones || zones.length === 0) return null

    return {
      labels: zones.map((z) => z.zone),
      datasets: [{
        label: "Users",
        data: zones.map((z) => z.count),
        backgroundColor: "#14b8a6",
      }],
    }
  }

  const getPainZonesChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false }, datalabels: { display: false } },
  })
  const openFreeSessionDrilldown = (stepId) => {
    const title = stepId === 'free_session_started' ? 'Free Session Started' : 'Free Session Completed'
    const rows = stepId === 'free_session_started'
      ? (data?.freeSessionStartedList || [])
      : (data?.freeSessionCompletedList || [])
    setAppKpiDrilldownTitle(title)
    setAppKpiDrilldownRows(rows)
    setAppKpiDrilldownKind(stepId)
    setAppKpiDrilldownOpen(true)
  }

  // =============================================================================
  // RENDER: FUNNEL
  // =============================================================================
  const renderFunnel = () => {
    const steps = getFunnelSteps()
    if (!steps || steps.length === 0) return null

    const maxCount = Math.max(...steps.map((s) => s.count || 0), 1)

    return (
      <div className="space-y-2">
        {steps.map((step, index) => {
		  const prevStep = steps.slice(0, index).reverse().find((s) => !s.halfStep);
		  const prevCount = prevStep ? prevStep.count : step.count;
		  const dropOff = !step.halfStep && prevCount > 0 ? ((prevCount - step.count) / prevCount * 100).toFixed(1) : 0;
          const widthPct = (step.count / maxCount) * 100
          const stepsToIgnored = ["recovery_score","completed_camera_selection","completed_mode_selection"]
          if(stepsToIgnored.includes(step.id)){
            return null
          }
          return (
            <div
              key={step.id}
              className={`funnel-step relative p-3 rounded-lg border cursor-pointer transition-all ${
                step.highlight
                  ? "bg-gradient-to-r from-teal-50 to-white border-teal-300"
                  : "bg-white border-slate-200 hover:border-teal-300"
              } ${
                step.halfStep
                  ? "funnel-step-half"
                  : ""
              }`}
              onClick={() => {
                if (step.id === 'free_session_started' || step.id === 'free_session_completed') {
                  openFreeSessionDrilldown(step.id)
                } else {
                  openDrillDown(step)
                }
              }}
            >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">{step.name}</span>
                {index > 0 && !step.halfStep && dropOff > 0 && (
                  <span className="text-xs text-red-500 shrink-0">-{dropOff}%</span>
                )}
              </div>
              <span className={`text-sm font-bold ${step.highlight ? "text-teal-600" : "text-slate-800"}`}>
                {step.count?.toLocaleString()}
              </span>
            </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    step.highlight ? "bg-teal-500" : "bg-teal-400"
                  }`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // =============================================================================
  // RENDER: SCREEN TIMING
  // =============================================================================
  const renderScreenTiming = () => {
    if (!data?.screenTiming) return null

    const maxDuration = Math.max(...data.screenTiming.map((s) => s.avg_duration_sec || 0), 1)
    const sortedScreenTiming = [...(data.screenTiming || [])].sort(
      (a, b) =>
        (SCREEN_ORDER[a.screen] ?? 999) -
        (SCREEN_ORDER[b.screen] ?? 999)
    );

    return (
      <div className="space-y-3">
        {sortedScreenTiming.map((screen) => {
          const widthPct = (screen.avg_duration_sec / maxDuration) * 100
          const isHighlight = ["test1_ar", "test1_demo", "test2_ar", "personalize"].includes(screen.screen)

          return (
            <div key={screen.screen}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">{SCREEN_LABELS[screen.screen] || screen.screen}</span>
                <span className={`font-medium ${isHighlight ? "text-amber-600" : "text-slate-800"}`}>
                  {formatDuration(screen.avg_duration_sec)}
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${isHighlight ? "bg-amber-500" : "bg-teal-500"}`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          )
        })}
        <p className="text-xs text-slate-400 mt-2 text-center">Highlighted = AR/video screens</p>
      </div>
    )
  }

  // =============================================================================
  // RENDER: COMPLETION LIKELIHOOD
  // =============================================================================
  const renderCompletionLikelihood = () => {
    if (!data?.completionLikelihood) return null

    return (
      <div className="space-y-3">
        {data.completionLikelihood.map((item) => (
          <div key={item.step}>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-slate-600">{item.label}</span>
              <span className={`text-sm font-bold ${item.isKeyMilestone ? "text-teal-600" : "text-slate-700"}`}>
                {item.likelihood}%
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-teal-500"
                style={{ width: `${item.likelihood}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  // =============================================================================
  // RENDER: MOVEMENT TEST ANALYSIS
  // =============================================================================
  const renderMovementTestAnalysis = () => {
    if (!data?.movementTests) return null
    console.log("[DATA IS ] : ",data)

    const renderTestCard = (test, testNum) => {
      if (!test) return null
      // const completionRate = test.started > 0 ? ((test.completed / test.started) * 100).toFixed(1) : 0
      const completionRate = test.started > 0 ? Math.min((test.completed / test.started) * 100, 100).toFixed(1) : 0;

      return (
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-700 mb-3">
            Movement Test {testNum}
            {testNum === 2 && <span className="text-xs text-slate-400 ml-2">(conditional)</span>}
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Started</span>
              <span className="font-semibold text-slate-800">{test.started?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Completed</span>
              <span className="font-semibold text-teal-600">{test.completed?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Dropped</span>
              <span className="font-semibold text-red-500">{test.dropped?.toLocaleString()}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
              <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${completionRate}%` }} />
            </div>
            <p className="text-xs text-slate-500 text-right">{completionRate}% completion rate</p>
          </div>
          {test.dropReasons && test.dropReasons.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-500 mb-2">Drop-off Reasons</p>
              <div className="space-y-1">
                {test.dropReasons.map((reason, i) => (
                  <div key={i} className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">{formatExitReason(reason.reason)}</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        reason.reason === "user_skipped"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {reason.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.movementTests.test1 && renderTestCard(data.movementTests.test1, 1)}
        {data.movementTests.test2 && renderTestCard(data.movementTests.test2, 2)}
      </div>
    )
  }

  // =============================================================================
  // RENDER: MYRA IMPACT
  // =============================================================================
  const renderMyraImpact = () => {
    if (!data?.myraImpact) return null

    const { landing, postPlan } = data.myraImpact
    const landingDiff = (landing?.withMyra || 0) - (landing?.withoutMyra || 0)
    const postPlanDiff = (postPlan?.withMyra || 0) - (postPlan?.withoutMyra || 0)

    const formatDiff = (diff, label) => {
      const absVal = Math.abs(diff).toFixed(1)
      if (diff > 0) return <span className="text-green-600">+{absVal}% {label}</span>
      if (diff < 0) return <span className="text-red-600">-{absVal}% {label}</span>
      return <span className="text-slate-400">0% {label}</span>
    }

    return (
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-purple-700">With Myra (Landing)</span>
            <span className="font-bold text-purple-800">{landing?.withMyra || 0}%</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Without Myra</span>
            <span className="font-medium text-slate-600">{landing?.withoutMyra || 0}%</span>
          </div>
          <p className="text-xs mt-1">
            {formatDiff(landingDiff, "GSA start rate")}
          </p>
        </div>
        <div className="border-t border-purple-100 pt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-purple-700">With Myra (Post-Plan)</span>
            <span className="font-bold text-purple-800">{postPlan?.withMyra || 0}%</span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Without Myra</span>
            <span className="font-medium text-slate-600">{postPlan?.withoutMyra || 0}%</span>
          </div>
          <p className="text-xs mt-1">
            {formatDiff(postPlanDiff, "signup rate")}
          </p>
        </div>
      </div>
    )
  }

  // =============================================================================
  // RENDER: APP TAB CONTENT
  // =============================================================================
  const APP_USERS_GRID_COLS =
  "grid-cols-[1fr_1fr_110px_120px_130px_80px_100px_100px_110px_120px_130px_130px_1fr]"
  const APP_USERS_GRID_COLS_PENDING =
  "grid-cols-[1fr_1fr_110px_120px_140px_140px_130px_130px_130px_1fr]"
 const renderAppTabContent = () => {
    const isPendingView = appUsersView === 'sessions' && appUsersSubview === 'pending'
    const appUsersGridCols = isPendingView ? APP_USERS_GRID_COLS_PENDING : APP_USERS_GRID_COLS
    // Sort variant options so an entry literally named "myptmyra" (if present) shows first,
    // right after the "All Variants" default option
    const sortedAppVariantOptions = [...variantOptions].sort((a, b) => {
      const aIsMyPTMyra = String(a).toLowerCase() === 'myptmyra'
      const bIsMyPTMyra = String(b).toLowerCase() === 'myptmyra'
      if (aIsMyPTMyra && !bIsMyPTMyra) return -1
      if (!aIsMyPTMyra && bIsMyPTMyra) return 1
      return 0
    })
    // Reuses the same linkOptions fetched on the GSA tab — no separate
    // App-tab source needed, same shortcut already used for variants above.
    const appLinkOptions = linkOptions

    return (
      <>
        {/* Filters Section - App Tab */}
      <section className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label
          htmlFor="date-range-app"
          className="text-sm font-medium text-slate-600 whitespace-nowrap"
        >
          Date Range
        </label>
        <select
          id="date-range-app"
          value={appDateRange}
          onChange={(e) => {
            setAppDateRange(e.target.value)
            if (e.target.value !== 'custom') {
              setAppCustomStartDate("")
              setAppCustomEndDate("")
            }
          }}
          className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="wtd">Week to Date</option>
          <option value="mtd">Month to Date</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
          <option value="custom">Custom Range</option>
        </select>
        {/* Custom Range Date Inputs */}
        {appDateRange === 'custom' && (
          <>
            <input
              type="date"
              value={appCustomStartDate}
              onChange={(e) => setAppCustomStartDate(e.target.value)}
              className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
            />
            <span className="text-slate-400 text-sm">to</span>
            <input
              type="date"
              value={appCustomEndDate}
              onChange={(e) => setAppCustomEndDate(e.target.value)}
              className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
            />
          </>
        )}
        <label
          htmlFor="site-filter-app"
          className="text-sm font-medium text-slate-600 whitespace-nowrap"
        >
          Site
        </label>
        <select
          id="site-filter-app"
          value={appSiteFilter}
          onChange={(e) => setAppSiteFilter(e.target.value)}
          className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
        >
          <option value="myptmyra">MyPTMyra</option>
          <option value="all">All Sites</option>
          <option value="startfreept">StartFreePT</option>
          <option value="virdio">Virdio Health</option>
        </select>
        <label
          htmlFor="variant-filter-app"
          className="text-sm font-medium text-slate-600 whitespace-nowrap"
        >
          Variant
        </label>
        <select
          id="variant-filter-app"
          value={appVariantFilter}
          onChange={(e) => setAppVariantFilter(e.target.value)}
          className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
        >
          <option value="all">All Variants</option>
          {sortedAppVariantOptions.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
        <label
          htmlFor="link-filter-app"
          className="text-sm font-medium text-slate-600 whitespace-nowrap"
        >
          Link Source
        </label>
        <select
          id="link-filter-app"
          value={appLinkFilter}
          onChange={(e) => setAppLinkFilter(e.target.value)}
          className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
        >
          <option value="all">All Link Sources</option>
          {appLinkOptions.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500"></span>
          <span className="text-slate-600">Free</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500"></span>
          <span className="text-slate-600">Paid</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          <span className="text-slate-600">Expired</span>
        </div>
      </div>
      </section>
        {appLoading ? (
          <div className="text-center py-12">
            <div className="text-slate-500">Loading app data...</div>
          </div>
        ) : (
          <>
{/* App KPI Cards - Row 1 */}
<section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">

  {/* Card 1: Signed Up */}
  <button
    onClick={() => openAppKpiDrilldown('signedUpUsers', 'Signed Up', 'user')}
    className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-4 shadow-sm border border-teal-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-teal-600 uppercase tracking-wide">Signed Up</p>
    <p className="mt-1 text-2xl font-bold text-teal-700">{appKpis?.signedUp?.toLocaleString() || 0}</p>
    <p className="text-xs text-teal-500 mt-1">New signups · click for plans</p>
  </button>
  {/* Card: Subscribed */}
  <button
    onClick={() => openAppKpiDrilldown('subscribedUsersList', 'Subscribed', 'subscribed')}
    className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 shadow-sm border border-emerald-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Subscribed</p>
    <p className="mt-1 text-2xl font-bold text-emerald-700">{appKpis?.subscribed?.toLocaleString() || 0}</p>
    <p className="text-xs text-emerald-500 mt-1">Purchased a plan · click for details</p>
  </button>

  {/* Card: Not Subscribed */}
  <button
    onClick={() => openAppKpiDrilldown('notSubscribedUsersList', 'Not Subscribed', 'not_subscribed')}
    className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-4 shadow-sm border border-orange-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">Not Subscribed</p>
    <p className="mt-1 text-2xl font-bold text-orange-700">{appKpis?.notSubscribed?.toLocaleString() || 0}</p>
    <p className="text-xs text-orange-500 mt-1">Selected "Not now" · click for feedback</p>
  </button>

  {/* Card 3: Active Users = completed a session in range */}
  <button
    onClick={() => openAppKpiDrilldown('activeUsersList', 'Active Users (Completed a Session)', 'user')}
    className="bg-gradient-to-br from-green-50 to-white rounded-xl p-4 shadow-sm border border-green-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Active Users</p>
    <p className="mt-1 text-2xl font-bold text-green-700">{appKpis?.activeUsers?.toLocaleString() || 0}</p>
    <p className="text-xs text-green-500 mt-1">Session completed in range</p>
  </button>

  {/* Card 5a: Active Paid — Weekly */}
  <button
    onClick={() => openAppKpiDrilldown('weeklyActiveUsersList', 'Active Paid — Weekly Plan', 'user')}
    className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-4 shadow-sm border border-indigo-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Active Paid — Weekly</p>
    <p className="mt-1 text-2xl font-bold text-indigo-700">{appKpis?.weeklyActiveUsers?.toLocaleString() || 0}</p>
    <p className="text-xs text-indigo-500 mt-1">Weekly subscribers</p>
  </button>

  {/* Card 5b: Active Paid — 8-Week */}
  <button
    onClick={() => openAppKpiDrilldown('eightWeekActiveUsersList', 'Active Paid — 8-Week Plan', 'user')}
    className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 shadow-sm border border-blue-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Active Paid — 8-Week</p>
    <p className="mt-1 text-2xl font-bold text-blue-700">{appKpis?.eightWeekActiveUsers?.toLocaleString() || 0}</p>
    <p className="text-xs text-blue-500 mt-1">8-week subscribers</p>
  </button>
  {/* Card: Auto-Renew ON */}
  <button
    onClick={() => openAppKpiDrilldown('autoRenewOnList', 'Auto-Renew ON', 'user')}
    className="bg-gradient-to-br from-cyan-50 to-white rounded-xl p-4 shadow-sm border border-cyan-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-cyan-600 uppercase tracking-wide">Auto-Renew ON</p>
    <p className="mt-1 text-2xl font-bold text-cyan-700">{appKpis?.autoRenewOn?.toLocaleString() || 0}</p>
    <p className="text-xs text-cyan-500 mt-1">Will renew automatically</p>
  </button>

  {/* Card: Auto-Renew OFF */}
  <button
    onClick={() => openAppKpiDrilldown('autoRenewOffList', 'Auto-Renew OFF', 'user')}
    className="bg-gradient-to-br from-rose-50 to-white rounded-xl p-4 shadow-sm border border-rose-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-rose-600 uppercase tracking-wide">Auto-Renew OFF</p>
    <p className="mt-1 text-2xl font-bold text-rose-700">{appKpis?.autoRenewOff?.toLocaleString() || 0}</p>
    <p className="text-xs text-rose-500 mt-1">Won't renew · includes cancelled</p>
  </button>
</section>

{/* App KPI Cards - Row 2 */}
<section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">

  {/* Card 6: Expired in last 30 days */}
  <button
    onClick={() => openAppKpiDrilldown('expiredUsersList', 'Expired Plans (Last 30 Days)', 'user')}
    className="bg-gradient-to-br from-red-50 to-white rounded-xl p-4 shadow-sm border border-red-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Expired (30d)</p>
    <p className="mt-1 text-2xl font-bold text-red-700">{appKpis?.expiredUsers?.toLocaleString() || 0}</p>
    <p className="text-xs text-red-500 mt-1">Most recent first</p>
  </button>

  {/* Card 7: Sessions Completed */}
  <button
    onClick={() => openAppKpiDrilldown('sessionsCompletedList', 'Sessions Completed', 'session')}
    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Sessions Completed</p>
    <p className="mt-1 text-2xl font-bold text-slate-800">{appKpis?.sessionsCompleted?.toLocaleString() || 0}</p>
    <p className="text-xs text-slate-400 mt-1">In selected date range</p>
  </button>

  {/* Card 8: Sessions Skipped */}
  <button
    onClick={() => openAppKpiDrilldown('sessionsSkippedList', 'Sessions Skipped', 'session')}
    className="bg-white rounded-xl p-4 shadow-sm border border-amber-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Sessions Skipped</p>
    <p className="mt-1 text-2xl font-bold text-amber-700">{appKpis?.sessionsSkipped?.toLocaleString() || 0}</p>
    <p className="text-xs text-amber-500 mt-1">In selected date range</p>
  </button>

  <button
    onClick={() => openChatModal("post_session")}
    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Chats Today</p>
    <p className="mt-1 text-2xl font-bold text-purple-700">{appKpis?.chatsToday?.toLocaleString() || 0}</p>
    <p className="text-xs text-purple-500 mt-1">In-app conversations · click for details</p>
  </button>
< button
    onClick={() => openAppKpiDrilldown('freeSessionStartedList', 'Free Session Started', 'free_session_started')}
    className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-4 shadow-sm border border-teal-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-teal-600 uppercase tracking-wide">Free Session Started</p>
    <p className="mt-1 text-2xl font-bold text-teal-700">{appKpis?.freeSessionStarted?.toLocaleString() || 0}</p>
    <p className="text-xs text-teal-500 mt-1">Click for time-since-signup</p>
  </button>

  <button
    onClick={() => openAppKpiDrilldown('freeSessionCompletedList', 'Free Session Completed', 'free_session_completed')}
    className="bg-gradient-to-br from-green-50 to-white rounded-xl p-4 shadow-sm border border-green-200 hover:shadow-md transition-shadow text-left"
  >
    <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Free Session Completed</p>
    <p className="mt-1 text-2xl font-bold text-green-700">{appKpis?.freeSessionCompleted?.toLocaleString() || 0}</p>
    <p className="text-xs text-green-500 mt-1">Click for session details</p>
  </button>
</section>

              <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden min-w-0">
              {/* Header — stays full width, never scrolls */}
              <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                <div className="mb-1">
                  <h2 className="text-lg font-semibold text-slate-800">
                    Users
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    Click a row to view GSA, session details, and chat history
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Sessions / Chats toggle */}
                  <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
                    <button
                      onClick={() => setAppUsersView('sessions')}
                      className={`px-3 py-2 text-xs font-semibold transition-colors ${
                        appUsersView === 'sessions'
                          ? 'bg-teal-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Sessions
                    </button>
                    <button
                      onClick={() => setAppUsersView('signed_up')}
                      className={`px-3 py-2 text-xs font-semibold transition-colors border-l border-slate-300 ${
                        appUsersView === 'signed_up'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      Signed Up
                    </button>
                  </div>

                  {/* Completed / Pending sub-toggle — only shown for Sessions view */}
                  {appUsersView === 'sessions' && (
                    <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
                      <button
                        onClick={() => setAppUsersSubview('completed')}
                        className={`px-3 py-2 text-xs font-semibold transition-colors ${
                          appUsersSubview === 'completed'
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        Completed
                      </button>
                      <button
                        onClick={() => setAppUsersSubview('pending')}
                        className={`px-3 py-2 text-xs font-semibold transition-colors border-l border-slate-300 ${
                          appUsersSubview === 'pending'
                            ? 'bg-amber-600 text-white'
                            : 'bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        Pending
                      </button>
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={appUsersSearchInput}
                    onChange={(e) => setAppUsersSearchInput(e.target.value)}
                    className="flex-1 min-w-0 text-sm border border-slate-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500"
                    style={{ minWidth: '150px' }}
                  />
                  <select
                    value={appUsersStatusFilter}
                    onChange={(e) => handleAppUsersStatusFilter(e.target.value)}
                    className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="F">Free</option>
                    <option value="P">Paid</option>
                    <option value="E">Expired</option>
                  </select>
                </div>
              </div>
              <p className="px-4 pt-3 text-xs text-slate-400">
                {appUsersView === 'sessions'
                  ? appUsersSubview === 'pending'
                    ? "Sessions scheduled in range that haven't been completed or skipped yet"
                    : 'Users with completed sessions or post-session chats in selected range'
                  : 'Users who signed up in the selected date range'}
              </p>
              <div
                style={{
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#94a3b8 #f1f5f9",
                }}
              >
              <div style={{ width: "100%", minWidth: isPendingView ? "1300px" : "1700px" }}>
                  {/* Column Headers */}
                  <div className={`grid items-center px-4 py-2 mt-2 bg-slate-50 border-b border-slate-200 ${appUsersGridCols}`}>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate pr-2 border-r border-slate-200">
                      Name
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate px-2">
                      Email
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                      Sign Up Date
                    </div>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                      Last Login
                    </div>
                    {isPendingView ? (
                      <>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Planned Date
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Session
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Dropped Off After
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Skipped Sub.
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Auto-Renew
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Feedback
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider truncate pr-2">
                          Subscription Plan
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Expired
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Sessions Done
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Sessions Missed
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Skipped Sub.
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Auto-Renew
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Free Session Started
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider pr-2">
                          Free Session Completed
                        </div>
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Feedback
                        </div>
                      </>
                    )}
                  </div>

                  {/* Rows */}
                  <div>
                    {appUsers.length === 0 ? (
                      <div className="text-center py-10 text-slate-400 text-sm">
                        {isPendingView ? "No pending sessions found." : "No users found."}
                      </div>
                    ) : isPendingView ? (
                      appUsers.map((user, idx) => (
                        <div
                          key={user.program_task_id || user.user_id || idx}
                          onClick={() => handleAppUserRowClick(user)}
                          className={`grid items-center px-4 py-2.5 bg-white hover:bg-slate-50 cursor-pointer border-b border-slate-200 ${appUsersGridCols}`}
                        >
                          <div className="text-sm font-medium text-slate-700 truncate pr-2 border-r border-slate-200">
                            {user.name || "—"}
                          </div>
                          <div className="text-xs text-slate-500 truncate px-2">
                            {user.email}
                          </div>
                          <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                            {formatDate(user.signup_date)}
                          </div>
                          <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                            {user.last_login ? formatDateTime(user.last_login) : <span className="text-slate-300">—</span>}
                          </div>
                          <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                            {formatDate(user.planned_date)}
                          </div>
                          <div className="text-xs text-slate-600 truncate pr-2">
                            {user.session_name || <span className="text-slate-300">—</span>}
                          </div>
                          <div className="text-xs pr-2">
                            {user.dropped_off_after ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                                {user.dropped_off_after.replace(/_/g, ' ')}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </div>
                          <div className="text-xs pr-2">
                            {user.skipped_subscription ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                                Yes
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-500">
                                No
                              </span>
                            )}
                          </div>
                          <div className="text-xs pr-2">
                            {user.auto_renew === null || user.auto_renew === undefined ? (
                              <span className="text-slate-300">—</span>
                            ) : user.auto_renew ? (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-700">
                                ON
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-700">
                                OFF
                              </span>
                            )}
                          </div>
                          <div
                            className="text-xs text-slate-600 truncate"
                            title={user.decline_feedback || ''}
                          >
                            {user.decline_feedback || <span className="text-slate-300 italic">—</span>}
                          </div>
                        </div>
                      ))
                    ) : (
                      appUsers.map((user, idx) => {
                        const planLabel = formatSubscriptionPlan(user?.product_name)
                        return (
                          <div
                            key={user.user_id || idx}
                            onClick={() => handleAppUserRowClick(user)}
                            className={`grid items-center px-4 py-2.5 bg-white hover:bg-slate-50 cursor-pointer border-b border-slate-200 ${appUsersGridCols}`}
                          >
                            <div className="text-sm font-medium text-slate-700 truncate pr-2 border-r border-slate-200">
                              {user.name || "—"}
                            </div>
                            <div className="text-xs text-slate-500 truncate px-2">
                              {user.email}
                            </div>
                            <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                              {formatDate(user.signup_date)}
                            </div>
                            <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                              {user.last_login ? formatDateTime(user.last_login) : <span className="text-slate-300">—</span>}
                            </div>
                            <div className="text-xs text-slate-600 truncate pr-2">
                              {planLabel || <span className="text-slate-300">—</span>}
                            </div>
                            <div className="pr-2">
                              {user.subscription_plan == null ? (
                                <span className="text-slate-300 text-xs">—</span>
                              ) : user.is_expired ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
                                  Yes
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                                  No
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-700 text-center pr-2 font-medium">
                              {user.session_count || 0}
                            </div>
                            <div className="text-sm text-center">
                              {user.sessions_missed > 0 ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
                                  {user.sessions_missed}
                                </span>
                              ) : (
                                <span className="text-slate-300">0</span>
                              )}
                            </div>
                            <div className="text-xs pr-2">
                              {user.skipped_subscription ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                                  Yes
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-500">
                                  No
                                </span>
                              )}
                            </div>
                            <div className="text-xs pr-2">
                              {user.auto_renew === null || user.auto_renew === undefined ? (
                                <span className="text-slate-300">—</span>
                              ) : user.auto_renew ? (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-700">
                                  ON
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-700">
                                  OFF
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                              {user.free_session_started_date
                                ? formatDateTime(user.free_session_started_date)
                                : <span className="text-slate-300">—</span>}
                            </div>
                            <div className="text-xs text-slate-500 pr-2 whitespace-nowrap">
                              {user.free_session_completed_date
                                ? formatDateTime(user.free_session_completed_date)
                                : <span className="text-slate-300">—</span>}
                            </div>
                            <div
                              className="text-xs text-slate-600 truncate"
                              title={user.feedback || ''}
                            >
                              {user.feedback || <span className="text-slate-300 italic">—</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Pagination — stays full width, never scrolls */}
              <div className="px-4 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
                <button
                  onClick={() =>
                    handleAppUsersPageChange(appUsersPagination.page - 1)
                  }
                  disabled={appUsersPagination.page <= 1}
                  className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ← Previous 50
                </button>
                <span className="text-sm text-slate-400">
                  {appUsersPagination.total === 0
                    ? "No results"
                    : `Showing ${
                        (appUsersPagination.page - 1) * 50 + 1
                      }–${Math.min(
                        appUsersPagination.page * 50,
                        appUsersPagination.total,
                      )} of ${appUsersPagination.total}`}
                </span>
                <button
                  onClick={() =>
                    handleAppUsersPageChange(appUsersPagination.page + 1)
                  }
                  disabled={
                    appUsersPagination.page * 50 >= appUsersPagination.total
                  }
                  className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next 50 →
                </button>
              </div>
            </section>
          </>
        )}
      </>
    );
  }

  // =============================================================================
  // RENDER: ERROR STATE
  // =============================================================================
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
          <h2 className="text-xl font-bold text-red-600">Connection Error</h2>
          <p className="text-slate-600 mt-2">
            Could not load dashboard data. Please ensure the backend server is running and accessible.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            <strong>Details:</strong> {error}
          </p>
          <button
            onClick={fetchDashboardData}
            className="mt-6 bg-teal-600 text-white font-semibold py-2 px-5 rounded-lg text-sm hover:bg-teal-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const downloadChatSessionsJSON = (sessions, touchpoint) => {
    if (!sessions?.length) return;
    
    const data = sessions.map(s => ({
      session_id: s.session_id || '',
      created_at: s.created_at || '',
      location: s.location || '',
      device: s.device || '',
      questions: s.question_count || 0,
      signed_up: s.signed_up || false,
      started_gsa: s.started_gsa || false,
      completed_gsa: s.completed_gsa || false,
    }));
    
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-sessions-${touchpoint}-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadChatDetailJSON = (messages, session) => {
    if (!messages?.length) return;
    
    const data = {
      session_id: session?.session_id || null,
      conversation_id: session?.conversation_id || null,
      location: session?.location || null,
      device: session?.device || null,
      user_id: session?.user_id || null,
      exported_at: new Date().toISOString(),
      messages: messages.map((m, i) => ({
        turn_number: i + 1,
        role: m.role || '',
        content: m.content || '',
        intent: m.intent || '',
        input_method: m.input_method || '',
        created_at: m.created_at || '',
      }))
    };
    
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${session?.session_id || 'unknown'}-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const openChatExportTab = (touchpoint, sessions) => {
      const touchpointLabels = { landing: 'Landing Page', post_plan: 'Post-Assessment', post_session: 'In-App (Post-Session)' };
      const label = touchpointLabels[touchpoint] || touchpoint;
      const rows = sessions.map(s => `
        <tr>
          <td>${formatDateTime(s.created_at)}</td>
          <td>${s.location || '—'}</td>
          <td>${s.device || '—'}</td>
          <td>${s.question_count || 0}</td>
          <td>${s.signed_up ? '<span class="badge green">Signed Up</span>' : s.completed_gsa ? '<span class="badge blue">Completed GSA</span>' : '—'}</td>
          <td><code>${(s.session_id || s.conversation_id || '').substring(0,12)}...</code></td>
        </tr>`).join('');
      const html = `<!DOCTYPE html><html><head><title>Chat Sessions — ${label}</title>
        <style>
          body { font-family: system-ui, sans-serif; padding: 24px; color: #1e293b; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          p { color: #64748b; font-size: 13px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #f8fafc; text-align: left; padding: 8px 12px; border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #94a3b8; }
          td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
          tr:hover td { background: #f8fafc; }
          .badge { padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; }
          .badge.green { background: #dcfce7; color: #15803d; }
          .badge.blue { background: #dbeafe; color: #1d4ed8; }
          code { font-size: 11px; color: #64748b; }
        </style></head><body>
        <h1>Myra Chat Sessions — ${label}</h1>
        <p>Exported ${new Date().toLocaleString()} · ${sessions.length} sessions shown (current page)</p>
        <table><thead><tr><th>Date/Time</th><th>Location</th><th>Device</th><th>Questions</th><th>Status</th><th>Session ID</th></tr></thead>
        <tbody>${rows}</tbody></table>
        </body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
  };
  const downloadDrilldownJSON = (users, stepData) => {
    if (!users?.length) return;
    
    const data = {
      step: stepData?.name || stepData?.id || 'step',
      step_id: stepData?.id || null,
      total_users: stepData?.count || users.length,
      exported_at: new Date().toISOString(),
      users: users.map(u => ({
        session_id: u.session_id || '',
        date_time: u.session_start_time || u.created_at || '',
        city: u.city || '',
        country: u.country || '',
        device: (u.device_type || '').split('|')[0]?.trim() || '',
        status: u.status || '',
        status_type: u.status_type || '',
        chat_touchpoints: u.chat_touchpoints || '',
        time_on_step_sec: u.time_on_step_sec || null,
        myra_engaged: u.myra_engaged || false,
        google_user: u.google_user || false,
        returning_user: u.returning_user || false,
        cancel_mic: u.cancel_mic || false,
        // Include exit data if available
        gsa_step: u.gsa_step || null,
        exit_reason: u.exit_reason || null,
        feedback: u.feedback || null,
      }))
    };
    
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drilldown-${stepData?.id || 'step'}-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openDrilldownTab = (users, stepData) => {
      if (!users?.length) return;
      const rows = users.map(u => `
        <tr>
          <td>${formatDateTime(u.session_start_time || u.created_at)}</td>
          <td>${[u.city, u.country].filter(Boolean).join(', ') || '—'}</td>
          <td>${(u.device_type || '').split('|')[0]?.trim() || '—'}</td>
          <td>${u.status ? `<span class="badge ${u.status_type === 'success' ? 'green' : u.status_type === 'exit' ? 'red' : 'gray'}">${u.status}</span>` : '—'}</td>
          <td>${u.chat_touchpoints ? `<span class="badge purple">${u.chat_touchpoints}</span>` : '—'}</td>
          <td>${u.time_on_step_sec != null ? formatDuration(u.time_on_step_sec) : '—'}</td>
          <td><code>${(u.session_id || '').substring(0,12)}...</code></td>
        </tr>`).join('');
      const html = `<!DOCTYPE html><html><head><title>Drilldown — ${stepData?.name}</title>
        <style>
          body { font-family: system-ui, sans-serif; padding: 24px; color: #1e293b; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          p { color: #64748b; font-size: 13px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #f8fafc; text-align: left; padding: 8px 12px; border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #94a3b8; }
          td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
          tr:hover td { background: #f8fafc; }
          .badge { padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; display: inline-block; }
          .badge.green { background: #dcfce7; color: #15803d; }
          .badge.red { background: #fee2e2; color: #b91c1c; }
          .badge.gray { background: #f1f5f9; color: #475569; }
          .badge.purple { background: #f3e8ff; color: #7e22ce; }
          code { font-size: 11px; color: #64748b; }
        </style></head><body>
        <h1>Drilldown: ${stepData?.name}</h1>
        <p>Exported ${new Date().toLocaleString()} · ${users.length} users (current page of ${stepData?.count?.toLocaleString()} total)</p>
        <table><thead><tr><th>Date/Time</th><th>Location</th><th>Device</th><th>Status</th><th>Chat</th><th>Time on Step</th><th>Session ID</th></tr></thead>
        <tbody>${rows}</tbody></table>
        </body></html>`;
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
  };

  const downloadSessionDetailJSON = (data) => {
    if (!data) return;
    
    // Clean up the data for JSON export
    const exportData = {
      session_id: data.session_id,
      session_start_time: data.session_start_time,
      device_type: data.device_type,
      device_info: data.device_info,
      location: {
        city: data.city,
        country: data.country,
      },
      status: {
        started_assessment: data.started_assessment || false,
        completed_gsa: data.viewed_plan || false,
        signed_up: data.signed_up || false,
      },
      clinical_profile: data.clinical_profile || {},
      demographics: data.demographics || {},
      movement_tests: data.movement_tests || {},
      recovery_plan: data.recovery_plan || {},
      journey_timeline: {
        total_screens: (data.screen_flow || []).length,
        total_duration_sec: Math.round((data.screen_flow || []).reduce((sum, s) => sum + (s.duration || 0), 0) / 1000),
        screens: (data.screen_flow || []).map(s => ({
          screen: s.screen,
          screen_label: SCREEN_LABELS[s.screen] || s.screen,
          duration_sec: Math.round((s.duration || 0) / 1000),
          enter_time: s.enter || null,
          completed: s.completed || false,
          exit_reason: s.exit_reason || null,
        }))
      },
      myra_conversations: (() => {
        const chats = data.chats || {};
        const result = {};
        Object.keys(chats).forEach(tp => {
          result[tp] = chats[tp].map(msg => ({
            role: msg.role,
            content: msg.content,
            intent: msg.intent,
            input_method: msg.input_method,
            created_at: msg.created_at,
          }));
        });
        return result;
      })(),
      exported_at: new Date().toISOString(),
    };
    
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-${(data.session_id || 'unknown').substring(0, 12)}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openSessionDetailTab = (data) => {
    if (!data) return;
    const screenFlow = data.screen_flow || [];
    const totalDuration = Math.round(screenFlow.reduce((sum, s) => sum + (s.duration || 0), 0) / 1000);
    const clinicalProfile = data.clinical_profile || {};
    const tests = data.movement_tests || {};
    const chats = data.chats || {};
    const status = data.signed_up ? 'Signed Up' : data.viewed_plan ? 'Completed GSA' : data.started_assessment ? 'Started GSA' : '';

    const SCREEN_LABELS_LOCAL = {
      landing: 'Landing Page', landing_video: 'Watched Landing Video', safety_screen: 'Safety Screen',
      problem_area: 'Problem Area', pain_location: 'Pain Location', cpg_questions: 'CPG Questions',
      subtype_questioning: 'Subtype Questions', self_assessment: 'Self Assessment', nprs_symptoms: 'NPRS/Symptoms',
      test1_demo: 'Demo Video 1', camera_preview: 'Camera Preview', test1_ar: 'Movement Test 1',
      test1_rpe: 'RPE', test1_symptoms: 'Symptoms (Test 1)', symptom1: 'Symptoms (Test 1)',
      test2_demo: 'Demo Video 2', test2_ar: 'Movement Test 2', test2_symptoms: 'Symptoms (Test 2)',
      symptom2: 'Symptoms (Test 2)', personalize: 'Demographics', recovery_score: 'Recovery Score',
      plan_screen: 'Plan Screen', screen_plan: 'Plan Screen',
    };

    const formatDur = (sec) => {
      if (!sec && sec !== 0) return '—';
      if (sec < 60) return `${sec}s`;
      const m = Math.floor(sec / 60), s = sec % 60;
      return s > 0 ? `${m}m ${s}s` : `${m}m`;
    };

    const journeyRows = screenFlow.map(s => {
      const dur = Math.round((s.duration || 0) / 1000);
      const enterTime = s.enter ? new Date(s.enter).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
      const dotColor = s.exit_reason ? '#ef4444' : s.completed ? '#14b8a6' : '#cbd5e1';
      return `<tr>
        <td><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${dotColor};margin-right:6px;"></span>${SCREEN_LABELS_LOCAL[s.screen] || s.screen}</td>
        <td>${formatDur(dur)}</td>
        <td>${enterTime}</td>
        <td>${s.completed ? '<span class="badge green">Yes</span>' : '<span class="badge gray">No</span>'}</td>
        <td>${s.exit_reason ? `<span class="badge red">${s.exit_reason.replace(/_/g, ' ')}</span>` : '—'}</td>
      </tr>`;
    }).join('');

    const clinicalRows = [
      ['Pain Zones', Array.isArray(clinicalProfile.selected_pain_zones) ? clinicalProfile.selected_pain_zones.join(', ') : '—'],
      ['CPG Classification', clinicalProfile.cpg_classification || '—'],
      ['Pain Subtype', (clinicalProfile.probable_subtype || '').replace(/_/g, ' ') || '—'],
      ['Pre-Test NPRS', clinicalProfile.pre_test_nprs ? `${clinicalProfile.pre_test_nprs}/10` : '—'],
      ['Symptom Duration', clinicalProfile.symptom_duration || '—'],
      ['Primary Goal', Array.isArray(clinicalProfile.primary_goal) ? clinicalProfile.primary_goal.map(g => g.text || g).join(', ') : (clinicalProfile.primary_goal || '—')],
      ['Floor Capability', clinicalProfile.floor_capability || '—'],
      ['Balance', clinicalProfile.balance || '—'],
    ].map(([k, v]) => `<tr><td class="label">${k}</td><td>${v}</td></tr>`).join('');

    const testRows = [tests.test1, tests.test2].filter(t => t?.name).map((t, i) => `
      <div class="card">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <strong>Test ${i + 1}: ${t.name}</strong>
          <span class="badge ${t.completed ? 'green' : 'red'}">${t.completed ? 'Completed' : 'Incomplete'}</span>
        </div>
        <table><tr>
          <td class="label">Symptom Response</td><td>${t.response || '—'}</td>
          <td class="label">RPE</td><td>${t.rpe != null ? `${t.rpe}/10` : '—'}</td>
          <td class="label">Video Watched</td><td>${t.video_watched_fully ? 'Yes' : 'No'}</td>
          ${t.exit_reason ? `<td class="label">Exit Reason</td><td>${t.exit_reason.replace(/_/g, ' ')}</td>` : ''}
        </tr></table>
      </div>`).join('');

    const touchpointLabels = { landing: 'Landing Page', post_plan: 'Post-Assessment', post_session: 'In-App (Post-Session)' };
    const chatHtml = ['landing', 'post_plan', 'post_session'].filter(tp => (chats[tp] || []).length > 0).map(tp => {
      const msgs = chats[tp];
      const bubbles = msgs.map(m => `
        <div style="display:flex;justify-content:${m.role === 'user' ? 'flex-end' : 'flex-start'};margin-bottom:8px">
          <div style="max-width:75%;background:${m.role === 'user' ? '#f3e8ff' : '#f8fafc'};border:1px solid ${m.role === 'user' ? '#e9d5ff' : '#e2e8f0'};border-radius:10px;padding:10px 14px">
            <div style="font-size:11px;font-weight:600;color:${m.role === 'user' ? '#7e22ce' : '#64748b'};margin-bottom:4px">
              ${m.role === 'user' ? '👤 User' : '🤖 Myra'}
              ${m.input_method && m.role === 'user' ? `<span style="font-weight:400;margin-left:6px;color:#94a3b8">${m.input_method}</span>` : ''}
            </div>
            <div style="font-size:13px;color:#334155">${m.content || ''}</div>
            ${m.intent && m.role === 'assistant' ? `<div style="font-size:11px;color:#94a3b8;margin-top:4px">Intent: ${m.intent}</div>` : ''}
          </div>
        </div>`).join('');
      return `<div style="margin-bottom:20px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
          <span style="width:8px;height:8px;border-radius:50%;background:#a855f7;display:inline-block"></span>
          <span style="font-size:11px;font-weight:600;color:#7e22ce;text-transform:uppercase">${touchpointLabels[tp]}</span>
          <span style="font-size:11px;color:#94a3b8">${msgs.filter(m => m.role === 'user').length} question(s)</span>
        </div>
        <div style="padding-left:16px;border-left:2px solid #f3e8ff">${bubbles}</div>
      </div>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>Session — ${data.session_id}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: system-ui, sans-serif; padding: 32px; color: #1e293b; max-width: 900px; margin: 0 auto; }
      h1 { font-size: 22px; margin-bottom: 4px; }
      .meta { color: #64748b; font-size: 13px; margin-bottom: 28px; font-family: monospace; }
      h2 { font-size: 15px; font-weight: 600; color: #0f172a; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; margin: 28px 0 14px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
      .kpi { background: #f8fafc; border-radius: 8px; padding: 12px 16px; }
      .kpi .k { font-size: 10px; text-transform: uppercase; color: #94a3b8; font-weight: 600; margin-bottom: 4px; }
      .kpi .v { font-size: 15px; font-weight: 700; color: #0f172a; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th { background: #f8fafc; text-align: left; padding: 8px 12px; border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #94a3b8; }
      td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
      td.label { color: #64748b; font-size: 12px; width: 180px; }
      .card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 12px; }
      .badge { padding: 2px 8px; border-radius: 99px; font-size: 11px; font-weight: 600; display: inline-block; }
      .badge.green { background: #dcfce7; color: #15803d; }
      .badge.red { background: #fee2e2; color: #b91c1c; }
      .badge.gray { background: #f1f5f9; color: #475569; }
      @media print { body { padding: 16px; } }
    </style></head><body>
    <h1>Session Details</h1>
    <div class="meta">${data.session_id} &nbsp;·&nbsp; ${new Date().toLocaleString()}</div>

    <h2>📋 Overview</h2>
    <div class="grid">
      <div class="kpi"><div class="k">Device</div><div class="v" style="font-size:13px;text-transform:capitalize">${data.device_type || '—'}</div></div>
      <div class="kpi"><div class="k">Location</div><div class="v" style="font-size:13px">${[data.city, data.country].filter(Boolean).join(', ') || '—'}</div></div>
      <div class="kpi"><div class="k">Session Started</div><div class="v" style="font-size:13px">${data.session_start_time ? new Date(data.session_start_time).toLocaleString() : '—'}</div></div>
      <div class="kpi"><div class="k">Status</div><div class="v" style="font-size:13px">${status || '—'}</div></div>
    </div>

    <h2>🗺️ Journey Timeline — ${screenFlow.length} screens · ${formatDur(totalDuration)} total</h2>
    <table>
      <thead><tr><th>Screen</th><th>Duration</th><th>Enter Time</th><th>Completed</th><th>Exit Reason</th></tr></thead>
      <tbody>${journeyRows}</tbody>
    </table>

    ${clinicalProfile.cpg_classification || clinicalProfile.pre_test_nprs ? `
    <h2>🩺 Clinical Profile</h2>
    <table><tbody>${clinicalRows}</tbody></table>` : ''}

    ${testRows ? `<h2>🏃 Movement Tests</h2>${testRows}` : ''}

    <h2>💬 Myra Conversations</h2>
    ${chatHtml || '<p style="color:#94a3b8;font-size:13px">No Myra conversations for this session.</p>'}

    </body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };
  const openChatDetailTab = (messages, session) => {
  if (!messages?.length) return;
  const sorted = [...messages].sort((a, b) => {
    const d = new Date(a.created_at) - new Date(b.created_at);
    return d !== 0 ? d : (a.turn_number || 0) - (b.turn_number || 0);
  });
  const touchpointLabels = { landing: 'Landing Page', post_plan: 'Post-Assessment', post_session: 'In-App (Post-Session)' };
  const bubbles = sorted.map(m => `
    <div style="display:flex;justify-content:${m.role === 'user' ? 'flex-end' : 'flex-start'};margin-bottom:12px">
      <div style="max-width:75%;background:${m.role === 'user' ? '#f3e8ff' : '#f8fafc'};border:1px solid ${m.role === 'user' ? '#e9d5ff' : '#e2e8f0'};border-radius:12px;padding:12px 16px">
        <div style="font-size:11px;font-weight:600;color:${m.role === 'user' ? '#7e22ce' : '#64748b'};margin-bottom:6px">
          ${m.role === 'user' ? '👤 User' : '🤖 Myra'}
          ${m.input_method && m.role === 'user' ? `<span style="font-weight:400;margin-left:6px;color:#94a3b8">${m.input_method}</span>` : ''}
        </div>
        <div style="font-size:14px;color:#334155;white-space:pre-wrap">${m.content || ''}</div>
        ${m.intent && m.role === 'assistant' ? `<div style="font-size:11px;color:#94a3b8;margin-top:6px">Intent: ${m.intent}</div>` : ''}
        <div style="font-size:11px;color:#cbd5e1;margin-top:6px">${new Date(m.created_at).toLocaleString()}</div>
      </div>
    </div>`).join('');

  const html = `<!DOCTYPE html><html><head><title>Conversation — ${session?.session_id || ''}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: system-ui, sans-serif; padding: 32px; color: #1e293b; max-width: 720px; margin: 0 auto; }
    h1 { font-size: 20px; margin-bottom: 4px; }
    .meta { color: #64748b; font-size: 13px; margin-bottom: 28px; }
    .meta span { margin-right: 16px; }
  </style></head><body>
  <h1>💬 Myra Conversation</h1>
  <div class="meta">
    <span>📍 ${session?.location || 'Unknown Location'}</span>
    <span>📱 ${session?.device || 'Unknown Device'}</span>
    ${session?.user_id ? `<span>👤 User: ${session.user_id}</span>` : ''}
    <span>🕐 ${new Date().toLocaleString()}</span>
  </div>
  ${bubbles}
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  };
    

  // =============================================================================
  // RENDER: MAIN
  // =============================================================================
  return (
    <div className="space-y-6" style={{ maxWidth: "calc(100vw - 20px)" }}>
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => setActiveTab("gsa")}
            className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
              activeTab === "gsa"
                ? "text-teal-600 border-b-2 border-teal-600 bg-teal-50"
                : "text-slate-500 border-b-2 border-transparent hover:text-teal-600 hover:bg-teal-50"
            }`}
          >
            📊 GSA KPI's & Analysis
          </button>
          <button
            onClick={() => setActiveTab("app")}
            className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
              activeTab === "app"
                ? "text-teal-600 border-b-2 border-teal-600 bg-teal-50"
                : "text-slate-500 border-b-2 border-transparent hover:text-teal-600 hover:bg-teal-50"
            }`}
          >
            📱 App KPI's & Analysis
          </button>
         <button
          onClick={() => setActiveTab("session")}
          className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
            activeTab === "session"
              ? "text-red-600 border-b-2 border-red-600 bg-red-50"
              : "text-slate-500 border-b-2 border-transparent hover:text-red-600 hover:bg-red-50"
          }`}
        >
          🗑️ Delete Session Data
        </button>
        <button
          onClick={() => setActiveTab("downloadAnalytics")}
          className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
            activeTab === "downloadAnalytics"
              ? "text-teal-600 border-b-2 border-teal-600 bg-teal-50"
              : "text-slate-500 border-b-2 border-transparent hover:text-teal-600 hover:bg-teal-50"
          }`}
        >
          ↓ Download Analytics
        </button>
        <button
          onClick={() => setActiveTab("downloadChats")}
          className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
            activeTab === "downloadChats"
              ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50"
              : "text-slate-500 border-b-2 border-transparent hover:text-purple-600 hover:bg-purple-50"
          }`}
        >
          ↓ Download Chats
        </button>
        <button
          onClick={() => setActiveTab("promos")}
          className={`px-6 py-3 font-semibold text-sm border-b-3 transition-all ${
            activeTab === "promos"
              ? "text-amber-600 border-b-2 border-amber-600 bg-amber-50"
              : "text-slate-500 border-b-2 border-transparent hover:text-amber-600 hover:bg-amber-50"
          }`}
        >
          Promos
        </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "gsa" ? (
        <>
          {/* GSA Tab - Filters Section */}
          <section className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label
                htmlFor="date-range"
                className="text-sm font-medium text-slate-600 whitespace-nowrap"
              >
                Date Range
              </label>
              <select
                id="date-range"
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value)
                  if (e.target.value !== 'custom') {
                    setCustomStartDate("")
                    setCustomEndDate("")
                  }
                }}
                className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="wtd">Week to Date</option>
                <option value="mtd">Month to Date</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="all">All Time</option>
                <option value="custom">Custom Range</option>
              </select>
              {/* C2: Two date inputs for custom range */}
              {dateRange === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
                  />
                  <span className="text-slate-400 text-sm">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
                  />
                </>
              )}
              <label
                htmlFor="site-filter"
                className="text-sm font-medium text-slate-600 whitespace-nowrap"
              >
                Site
              </label>
              <select
                id="site-filter"
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
              >
                <option value="myptmyra">MyPTMyra</option>
                <option value="all">All Sites</option>
                <option value="startfreept">StartFreePT</option>
                <option value="virdio">Virdio Health</option>
              </select>
              {/* C1: Variant picker */}
              <label
                htmlFor="variant-filter"
                className="text-sm font-medium text-slate-600 whitespace-nowrap"
              >
                Variant
              </label>
              <select
                id="variant-filter"
                value={variantFilter}
                onChange={(e) => setVariantFilter(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
              >
                <option value="all">All Variants</option>
                {variantOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              {/* From-the-link picker */}
              <label
                htmlFor="link-filter"
                className="text-sm font-medium text-slate-600 whitespace-nowrap"
              >
                Link Source
              </label>
              <select
                id="link-filter"
                value={linkFilter}
                onChange={(e) => setLinkFilter(e.target.value)}
                className="rounded-lg border-slate-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm px-3 py-2 bg-white border"
              >
                <option value="all">All Link Sources</option>
                {linkOptions.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              
              {/* <button
                onClick={handleDownloadAnalytics}
                disabled={loading}
                className="text-xs px-3 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title="Download analytics sessions for current date range as JSON"
              >
                {loadingDownloadAnalytics ? "Downloading..." : "↓ Download Analytics"}
              </button> */}
              {/* <button
                onClick={handleDownloadChatHistory}
                disabled={loading}
                className="text-xs px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title="Download chat history for current date range as JSON"
              >
                ↓ Download Chats
              </button> */}
              <button
                onClick={handleViewAnalytics}
                disabled={loading}
                className="text-xs px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                title="View all analytics in a new tab"
              >
                ↗ View Analytics
              </button>
            </div>
          </section>

          {loading ? (
            <div className="text-center py-12">
              <div className="text-slate-500">Loading dashboard data...</div>
            </div>
          ) : (
            data && (
              <>
                {/* ============================================================= */}
                {/* GROUP 1 — Funnel Cards (clickable with drilldown)              */}
                {/* ============================================================= */}


                <section>
                  <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Funnel</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {(data.funnelStepsTop || []).map((step, index) => (
                    <button
                      key={step.id}
                      // onClick={() => openDrillDown(step)}
                      onClick={() => {
                        if (step.id === 'free_session_started' || step.id === 'free_session_completed') {
                          openFreeSessionDrilldown(step.id)
                        } else {
                          openDrillDown(step)
                        }
                      }}
                      className={`${index === 0 || step.highlight ? "bg-gradient-to-br from-teal-50 to-white border-2 border-teal-200 hover:border-teal-400" : "bg-white border border-slate-200 hover:border-teal-400"} rounded-xl p-4 shadow-sm transition-all cursor-pointer text-left`}
                    >
                      <p className={`text-xs font-medium uppercase tracking-wide ${index === 0 || step.highlight ? "text-teal-600" : "text-slate-500"}`}>{step.label || step.name}</p>
                      <p className={`mt-1 text-2xl font-bold ${step.highlight ? "text-teal-600" : index === 0 ? "text-slate-800" : "text-slate-800"}`}>{step.count?.toLocaleString() || 0}</p>
                      <p className={`text-xs mt-1 ${index === 0 || step.highlight ? "text-teal-500" : "text-slate-400"}`}>{step.description || step.name}</p>
                    </button>
                  ))}
                  </div>
                </section>
                

                {/* ============================================================= */}
                {/* GROUP 2 — Ads Traffic (clickable with drilldown)               */}
                {/* ============================================================= */}
                <section>
                  <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Ads Traffic</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <button
                    onClick={() => openDrillDown({ id: "google_traffic", name: "Google Traffic", count: data.kpis?.googleTraffic || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-blue-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Google Traffic</p>
                    <p className="mt-1 text-2xl font-bold text-blue-600">{data.kpis?.googleTraffic?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Sessions with variant</p>
                  </button>
                  <button
                    onClick={() => openDrillDown({ id: "returning_users", name: "Repeat Traffic", count: data.kpis?.isUserCameBackAgain || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-purple-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Repeat Traffic</p>
                    <p className="mt-1 text-2xl font-bold text-purple-600">{data.kpis?.isUserCameBackAgain?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Returning users</p>
                  </button>
                  <button
                    onClick={() => openDrillDown({ id: "visited_how_it_works", name: "Visited How It Works", count: data.kpis?.visitedHowItWorks || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">How It Works</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.visitedHowItWorks?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">{data.kpis?.totalSessions ? ((data.kpis.visitedHowItWorks / data.kpis.totalSessions) * 100).toFixed(1) : 0}% of visitors</p>
                  </button>
                  <button
                    onClick={() => openDrillDown({ id: "visited_ask_myra", name: "Visited Ask Myra", count: data.kpis?.visitedAskMyra || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ask Myra Questions</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.visitedAskMyra?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">{data.kpis?.totalSessions ? ((data.kpis.visitedAskMyra / data.kpis.totalSessions) * 100).toFixed(1) : 0}% of visitors</p>
                  </button>
                  <button
                    onClick={() => openDrillDown({ id: "visited_about", name: "Visited About Page", count: data.kpis?.visitedAbout || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">About Us</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.visitedAbout?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">{data.kpis?.totalSessions ? ((data.kpis.visitedAbout / data.kpis.totalSessions) * 100).toFixed(1) : 0}% of visitors</p>
                  </button>
                  <button
                    onClick={() => openDrillDown({ id: "user_move_between_subpage", name: "Moved Between Pages", count: data.kpis?.userMoveBetweenSubPage || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Moved Between Pages</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.userMoveBetweenSubPage?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">{data.kpis?.totalSessions ? ((data.kpis.userMoveBetweenSubPage / data.kpis.totalSessions) * 100).toFixed(1) : 0}% of visitors</p>
                  </button>
                  <button
                    onClick={() => openDrillDown({ id: "came_to_landing_page", name: "Returned to Landing", count: data.kpis?.cameToLandingPage || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Returned to Landing</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.cameToLandingPage?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">From sub-pages</p>
                  </button>
                  </div>
                </section>

                {/* ============================================================= */}
                {/* GROUP 3 — Ratios (display only, not clickable)                 */}
                {/* ============================================================= */}
                <section>
                  <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Ratios</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Started GSA %</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">{data.kpis?.startRate || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Started GSA ÷ Landed</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Conversion %</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">{data.kpis?.conversionRate || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Landed → Safety Screen</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Test 1 Completion %</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{data.kpis?.test1CompletionRate || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Of those who started</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Completion %</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">{data.kpis?.completionRate || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Started GSA → Recovery Plan</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Sign Up %</p>
                    <p className="mt-1 text-2xl font-bold text-teal-600">{data.kpis?.signUpRate || 0}%</p>
                    <p className="text-xs text-slate-400 mt-1">Started GSA → Signed Up</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg Time on Landing</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{formatDuration(data.kpis?.avgTimeLanding)}</p>
                    <p className="text-xs text-slate-400 mt-1">Before GSA start</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg GSA Duration</p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">{formatDuration(data.kpis?.avgGsaDuration)}</p>
                    <p className="text-xs text-slate-400 mt-1">Start to completion</p>
                  </div>
                  </div>
                </section>

                {/* ============================================================= */}
                {/* GROUP 4 — Myra Comm Mode (clickable with drilldown)            */}
                {/* ============================================================= */}
                <section>
                  <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Myra Comm Mode</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {/* <button
                    onClick={() => openDrillDown({ id: "user_uses_voice", name: "Used Voice Input", count: data.kpis?.userUsesVoice || 0, section: "landing", myraTouch: null })}
                    className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 shadow-sm border border-blue-200 hover:border-blue-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Voice</p>
                    <p className="mt-1 text-2xl font-bold text-blue-700">{data.kpis?.userUsesVoice?.toLocaleString() || 0}</p>
                    <p className="text-xs text-blue-500 mt-1">Used voice input</p>
                  </button> */}
                  <button
                    onClick={() => openDrillDown({ id: "user_uses_chat", name: "Used Chat Input", count: data.kpis?.userUsesChat || 0, section: "landing", myraTouch: null })}
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-200 hover:border-purple-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Chat</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">{data.kpis?.userUsesChat?.toLocaleString() || 0}</p>
                    <p className="text-xs text-purple-500 mt-1">Used text chat</p>
                  </button>
                  {/* <button
                    onClick={() => openDrillDown({ id: "used_camera", name: "Used Camera", count: data.kpis?.usedCamera || 0, section: "gsa", myraTouch: null })}
                    className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-4 shadow-sm border border-teal-200 hover:border-teal-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-teal-600 uppercase tracking-wide">Camera</p>
                    <p className="mt-1 text-2xl font-bold text-teal-700">{data.kpis?.usedCamera?.toLocaleString() || 0}</p>
                    <p className="text-xs text-teal-500 mt-1">Used camera for movement test</p>
                  </button> */}
                  <button
                    onClick={() => openDrillDown({ id: "user_declines_camera", name: "Declined Camera", count: data.kpis?.userDeclinesCamera || 0, section: "landing", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-red-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">No Camera</p>
                    <p className="mt-1 text-2xl font-bold text-red-600">{data.kpis?.userDeclinesCamera?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Camera permission denied</p>
                  </button>
                  {/* <button
                    onClick={() => openDrillDown({ id: "camera_switched_chat_to_voice", name: "Switched to Voice — Camera Users", count: data.kpis?.cameraSwitchedChatToVoice || 0, section: "test", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-blue-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Switched to Voice — Camera Users</p>
                    <p className="mt-1 text-2xl font-bold text-blue-700">{data.kpis?.cameraSwitchedChatToVoice?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Camera users who switched from chat to voice</p>
                  </button> */}
                  {/* <button
                    onClick={() => openDrillDown({ id: "no_camera_switched_chat_to_voice", name: "Switched to Voice — No-Camera Users", count: data.kpis?.noCameraSwitchedChatToVoice || 0, section: "test", myraTouch: null })}
                    className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-blue-400 transition-all cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Switched to Voice — No-Camera Users</p>
                    <p className="mt-1 text-2xl font-bold text-blue-700">{data.kpis?.noCameraSwitchedChatToVoice?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">No-camera users who switched from chat to voice</p>
                  </button> */}
                  </div>
                </section>

                {/* ============================================================= */}
                {/* GROUP 5 — Myra Chats (clickable, opens chat modal)             */}
                {/* ============================================================= */}
                <section>
                  <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Myra Chats</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button
                    onClick={() => openChatModal("all")}
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Chat Total</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">{data.kpis?.myraChatCount?.toLocaleString() || 0}</p>
                    <p className="text-xs text-purple-500 mt-1">All Myra conversations</p>
                  </button>
                  <button
                    onClick={() => openChatModal("landing")}
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Chat on Landing</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">{data.kpis?.myraLandingCount?.toLocaleString() || 0}</p>
                    <p className="text-xs text-purple-500 mt-1">Talk / chat on landing page</p>
                  </button>
                  <button
                    onClick={() => openChatModal("post_plan")}
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Chat on Recovery</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">{data.kpis?.myraPostPlanCount?.toLocaleString() || 0}</p>
                    <p className="text-xs text-purple-500 mt-1">Chats on recovery plan page</p>
                  </button>
                  <button
                    onClick={() => openChatModal("post_session")}
                    className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-4 shadow-sm border border-purple-100 hover:shadow-md transition-shadow cursor-pointer text-left"
                  >
                    <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Myra Chat (In-App)</p>
                    <p className="mt-1 text-2xl font-bold text-purple-700">{data.kpis?.myraPostSessionCount?.toLocaleString() || 0}</p>
                    <p className="text-xs text-purple-500 mt-1">Post-session chats</p>
                  </button>
                  </div>
                </section>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Detailed Funnel (2/3 width) */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Detailed Journey Funnel */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-800">
                            User Journey Funnel
                          </h2>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Click any step to see individual users
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-3 py-1.5 rounded-lg font-medium bg-teal-100 text-teal-700">
                            Detailed
                          </span>
                        </div>
                      </div>
                      {renderFunnel()}
                    </section>

                    {/* Movement Test Analysis */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <h2 className="text-lg font-semibold text-slate-800 mb-4">
                        Movement Test Analysis
                      </h2>
                      {renderMovementTestAnalysis()}
                    </section>

                  </div>

                  {/* Right Column: Insights (1/3 width) */}
                  <div className="space-y-6">
                    {/* Screen Time Analysis */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <h2 className="text-lg font-semibold text-slate-800 mb-4">
                        Avg. Time Per Screen
                      </h2>
                      {renderScreenTiming()}
                    </section>

                    {/* Point of No Return */}
                    <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                      <h2 className="text-lg font-semibold text-slate-800 mb-2">
                        Completion Likelihood
                      </h2>
                      <p className="text-xs text-slate-500 mb-4">
                        If user reaches this step, what's the chance they
                        complete GSA?
                      </p>
                      {renderCompletionLikelihood()}
                    </section>

                    {/* Myra Impact Preview */}
                    <section className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 shadow-sm border border-purple-100">
                      <h2 className="text-lg font-semibold text-purple-800 mb-2">
                        Myra Impact
                      </h2>
                      <p className="text-xs text-purple-600 mb-4">
                        Conversion comparison
                      </p>
                      {renderMyraImpact()}
                    </section>
                  </div>
                </div>

                {/* Charts Row */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      Clinical Profiles (CPG)
                    </h3>
                    <div className="h-64">
                      {getCpgChartData() ? (
                        <Doughnut
                          data={getCpgChartData()}
                          options={getCpgChartOptions()}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                          No Data
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      Symptom Response (Test 1)
                    </h3>
                    <div className="h-64">
                      {getSymptomChartData() ? (
                        <Bar
                          data={getSymptomChartData()}
                          options={getSymptomChartOptions()}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                          No Data
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      Device Distribution
                    </h3>
                    <div className="h-64">
                      {getDeviceChartData() ? (
                        <Doughnut
                          data={getDeviceChartData()}
                          options={getDeviceChartOptions()}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                          No Data
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* Pain Zones Chart */}
                <section className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    Top Problem Areas Selected
                  </h3>
                  <div className="h-80">
                    {getPainZonesChartData() ? (
                      <Bar
                        data={getPainZonesChartData()}
                        options={getPainZonesChartOptions()}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-slate-500 font-medium">
                        No Data
                      </div>
                    )}
                  </div>
                </section>

                {/* C6: SQL Query Runner */}
                <CollapsibleSection
                  id="sql-runner"
                  title="🔍 SQL Query Runner"
                  defaultExpanded={false}
                  badge="SELECT only"
                >
                  <div className="space-y-4">
                    <textarea
                      value={queryText}
                      onChange={(e) => setQueryText(e.target.value)}
                      placeholder="SELECT * FROM assessment_funnel_analytics WHERE created_at >= '2026-03-24' LIMIT 100"
                      rows={4}
                      className="w-full font-mono text-sm border border-slate-300 rounded-lg p-3 focus:border-teal-500 focus:ring-teal-500"
                    />
                    <div className="flex items-center gap-3">
                      <button
                        onClick={runCustomQuery}
                        disabled={!queryText.trim() || queryLoading}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {queryLoading ? 'Running...' : 'Run Query'}
                      </button>
                      {queryResults?.rows?.length > 0 && (
                        <button
                          onClick={exportQueryCSV}
                          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 border border-slate-300"
                        >
                          Export CSV ({queryResults.rowCount} rows)
                        </button>
                      )}
                      {queryError && (
                        <span className="text-sm text-red-600">{queryError}</span>
                      )}
                    </div>

                    {queryResults?.rows?.length > 0 && (
                      <div className="overflow-x-auto max-h-96 border border-slate-200 rounded-lg">
                        <table className="min-w-full divide-y divide-slate-200 text-sm">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              {queryResults.columns.map(col => (
                                <th key={col} className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase whitespace-nowrap">
                                  {col}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-100">
                            {queryResults.rows.map((row, idx) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                {queryResults.columns.map(col => (
                                  <td key={col} className="px-3 py-2 whitespace-nowrap text-slate-600 max-w-[200px] truncate" title={String(row[col] ?? '')}>
                                    {row[col] !== null && row[col] !== undefined ? String(row[col]) : '—'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {queryResults?.rowCount === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">No results found.</p>
                    )}

                    <p className="text-xs text-slate-400">
                      Allowed tables: assessment_funnel_analytics, assessment_funnel_analytics_data. SELECT only. Max 5,000 rows.
                    </p>
                  </div>
                </CollapsibleSection>
              </>
            )
          )}
        </>
      ) : activeTab === "app" ? (
        renderAppTabContent()
      ) : activeTab === "session" ? (
        <SessionDataManager variantOptions={variantOptions} linkOptions={linkOptions} />
      ) : activeTab === "downloadAnalytics" ? (
        <DownloadAnalyticsTab
          startDate={startDate}
          endDate={endDate}
          siteFilter={siteFilter}
          variantFilter={variantFilter}
          variantOptions={variantOptions}
          linkOptions={linkOptions}
        />
        ) : activeTab === "downloadChats" ? (
        <DownloadChatsTab
          startDate={startDate}
          endDate={endDate}
          siteFilter={siteFilter}
          variantFilter={variantFilter}
          variantOptions={variantOptions}
          linkOptions={linkOptions}
        />
      ) : (
        <PromosTab />
      )}

      {/* Drill-Down Modal */}
      <DrillDownModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        stepData={modalStepData}
        users={modalUsers}
        loading={modalLoading}
        pagination={modalPagination}
        onPageChange={handlePageChange}
        onSessionClick={handleSessionClick}
        onDownload={() => downloadDrilldownJSON(modalUsers, modalStepData)}
        onNewTab={() => openDrilldownTab(modalUsers, modalStepData)}
      />

      {/* Session-Details Modal */}
      <SessionDetailModal
        isOpen={sessionDetailModalOpen}
        onClose={() => setSessionDetailModalOpen(false)}
        sessionDetails={selectedSessionDetails}
        loading={sessionDetailsLoading}
        onDownload={() => downloadSessionDetailJSON(selectedSessionDetails)}
        onNewTab={() => openSessionDetailTab(selectedSessionDetails)}
      />

      {/* Chat Sessions Modal */}
      <ChatSessionsModal
        isOpen={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        touchpoint={chatTouchpoint}
        sessions={chatSessions}
        loading={chatLoading}
        pagination={chatPagination}
        onPageChange={handleChatPageChange}
        onSessionClick={openChatDetail}
      />

      {/* Chat Detail Modal */}
      <ChatDetailModal
        isOpen={chatDetailModalOpen}
        onClose={() => setChatDetailModalOpen(false)}
        session={selectedChatSession}
        loading={chatDetailsLoading}
        messages={chatMessages}
        onNewTab={() => openChatDetailTab(
          [...chatMessages].sort((a, b) => {
            const d = new Date(a.created_at) - new Date(b.created_at);
            return d !== 0 ? d : (a.turn_number || 0) - (b.turn_number || 0);
          }),
          selectedChatSession
        )}
      />

      {/* App User Detail Modal */}
      <AppUserDetailModal
        isOpen={appUserDetailModalOpen}
        onClose={() => setAppUserDetailModalOpen(false)}
        user={selectedAppUser}
        gsaData={selectedAppUserGSA}
        sessions={selectedAppUserSessions}
        loading={appUserDetailLoading}
        onSessionClick={handleAppSessionRowClick}
      />
      {/* App Tab — Generic KPI Drilldown Modal */}
      <AppKpiDrilldownModal
        isOpen={appKpiDrilldownOpen}
        onClose={() => setAppKpiDrilldownOpen(false)}
        title={appKpiDrilldownTitle}
        rows={appKpiDrilldownRows}
        kind={appKpiDrilldownKind}
        // onRowClick={appKpiDrilldownKind === 'free_session' ? handleFreeSessionRowClick : handleAppDrilldownRowClick}
        onRowClick={handleAppDrilldownRowClick}
        onDownload={downloadAppKpiDrilldownJSON}
        onNewTab={openAppKpiDrilldownTab}
        queryDateLabel={formatAppQueryDateRange()}
      />

      {/* App Session Detail Modal */}
      <AppSessionDetailModal
        isOpen={appSessionDetailModalOpen}
        onClose={() => setAppSessionDetailModalOpen(false)}
        session={selectedAppSession}
        sets={selectedAppSessionSets}
        chat={selectedAppSessionChat}
        loading={appSessionDetailLoading}
      />

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}</style>
    </div>
  );
}

export default AnalyticsPage